import dayjs from 'dayjs';
import _ from 'lodash';

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { v4 as uuid4 } from 'uuid';


import dynamicAttributesDataSet from 'data/dynamic-attributes-data-set.json';
import mystiqueInputsBasedOnCVP from 'data/mystique-inputs-based-on-cvp.json';
import {
  CVP_BASED_ON_VERTICAL_AND_COUNTRY_ID,
  CVP_DISPLAY_NAMES,
} from 'data/smart-campaign';
import {
  CALL_TO_ACTION_MAP,
  channelOptions,
  COUNTRY_ID_VS_COUNTRY_NAME,
  CREATE_SMART_CAMPAIGN_DEFAULTS,
  DOUBLE_BRACKET_CONTENT_EXTRACTOR,
  DYNAMIC_ATTRIBUTES_LABEL_TO_VALUES_MAP,
  DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP,
  FIELDS_TO_VALIDATE_FOR_GENERATE_CONTENTS,
  HEDWIG_TEMPLATE_VARIABLES,
  INVALID_PERSONAL_ATTRIBUTE_STYLE,
  MYSTIQUE_META_DATA_FIELD_NAMES,
  OTHERS_OF_CVP,
  PERSONAL_ATTRIBUTE_VALUE_MATCHER,
  PN_IMAGE_KEY,
  PRODUCT_OVERVIEW_MAP,
  PUSH_CHANNEL_ACTION_OPTIONS,
  PUSH_CHANNEL_ACTIONS,
  REASON_TO_USE_MAP,
  SINGLE_BRACKET_CONTENT_EXTRACTOR,
  SLANG_DETAILS_BASED_ON_COUNTRY_AND_LANGUAGE,
  SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG,
  SMART_CAMPAIGN_CHANNELS,
  SMART_CAMPAIGN_CONTENT_SOURCE_MODES,
  SMART_CAMPAIGN_FORM_FIELD_LABELS,
  SMART_CAMPAIGN_FORM_KEYS,
  SMART_CAMPAIGN_MAX_IMAGE_SIZE,
  SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
  SMART_CAMPAIGN_MYSTIQUE_CRM,
  SMART_CAMPAIGN_MYSTIQUE_CRM_l1_PERSONA,
  UCD_MYSTIQUE_CHANNEL_NAMES,
  UNKNOWN_ATTRIBUTE,
  VALID_PERSONAL_ATTRIBUTE_STYLE,
} from 'omega-rtc/constants/ucd';
import supportedLanguages from 'omega-rtc/data/languages.json';
import { SmartCampaignStatus } from 'omega-rtc/data/trident-campaigns';
import {
  productDisplayName,
  productFromApi,
} from 'omega-rtc/views/ucd/content-config/adapters';
import type { ProductBodyApi } from 'omega-rtc/types/ts/content-config';
import {
  generateCampaignCode,
  getCountryCodeFromCountryID,
  getCountryTimezonesFromCountryID,
  getRoundNumberFromSmartCommsRoundName,
  getSmartCommsRoundNameFromNumber,
  getSmartCommsVariantNameFromNumber,
  getVariantNumberFromSmartCommsVariantName,
} from 'omega-rtc/utils/ucd-utils';

import { convertTimeWindowToHoursInUTC } from 'omega-rtc/utils/time-utils';
import completeSetupIcon from 'img/ucd_campaign_complete_setup.svg';
import pendingApprovalIcon from 'img/ucd_campaign_pending_approval.svg';

import scribe from '@grab-x/scribe-web-sdk';
import ScribeEventName from 'constants/scribe';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(utc);
dayjs.extend(timezone);

// Type definitions
type ImageMetaData = {
  url?: string;
  size?: number;
  status?: string;
  name: string;
  width?: number;
  height?: number;
  uid?: string;
};

type ImageInfoCallback = (imageMetaData: ImageMetaData) => void;

/**
 * Function to check the given URL is valid.
 */
export const isImageUrlValid = (imageUrl: string): boolean => {
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/.test(imageUrl);
};

/**
 * Function to get the image information from URL.
 */
export const getImageInfoFromUrl = async (
  imageUrl: string,
  callback: ImageInfoCallback,
): Promise<void> => {
  let imageMetaData: ImageMetaData = {
    name: '',
    size: 0,
    url: imageUrl,
    uid: '',
  };
  const urlParts = imageUrl.split('/').pop();
  const name = urlParts ? urlParts.split('?')[0] : '';
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const imageBitmap = await createImageBitmap(blob);
    const { width, height } = imageBitmap;
    const size = blob && blob.size;

    /**
     * (size > SMART_CAMPAIGN_MAX_IMAGE_SIZE * 1000) -> Is done to ensure image size is less than 250kb.
     * Note:
     * size will be total number of bytes
     * SMART_CAMPAIGN_MAX_IMAGE_SIZE * 1000 is done to convert KB to bytes.
     */
    if (size && size > SMART_CAMPAIGN_MAX_IMAGE_SIZE * 1000) {
      imageMetaData = {
        url: undefined,
        size,
        status: 'failed',
        name,
      };
    } else {
      imageMetaData = {
        url: imageUrl,
        size,
        name,
        width,
        height,
      };
    }
    callback(imageMetaData);
  } catch (error) {
    if (error) {
      imageMetaData = {
        url: imageUrl,
        name,
      };
      callback(imageMetaData);
    }
  }
};

// Additional type definitions for Batch 1
type MetaDataForPushChannel = {
  imageUrl?: string;
  actionType?: string;
  actionURL?: string;
  isActionTypeOrURLUpdated?: boolean;
  isPNImageUrlUpdated?: boolean;
  [key: string]: any;
};

type MetaDataForPCIChannel = {
  appName?: string;
  inboxTitle?: Record<string, string>;
  inboxMessage?: Record<string, string>;
  inboxSubtitle?: Record<string, string>;
  inboxCategory?: string;
  inboxButtonText?: Record<string, string>;
  inboxButtonLink?: Record<string, string>;
  inboxCoverImageUrl?: Record<string, string>;
  [key: string]: any;
};

type PrimaryLanguageContentsInForm = {
  rounds: Record<string, any>;
  selectedControlGroupVariant?: string;
};

type LanguageData = {
  language: string;
  slang?: string;
  payload: any;
};

const supportedLanguageCodeByLowerCase = _.keyBy(
  _.keys(supportedLanguages),
  (supportedLanguageCode) => supportedLanguageCode.toLowerCase(),
);
const supportedLanguageCodeByLabel = _.keyBy(
  _.keys(supportedLanguages),
  (supportedLanguageCode) => supportedLanguages[supportedLanguageCode].label.toLowerCase(),
);

export const normalizeSmartCommsLanguageCode = (
  languageCode: string = '',
): string => {
  if (!languageCode) {
    return '';
  }

  const lowerCaseLanguageCode = languageCode.toLowerCase();
  return (
    supportedLanguageCodeByLowerCase[lowerCaseLanguageCode] ||
    lowerCaseLanguageCode
  );
};

const normalizeSmartCommsLanguageCodes = (
  languageCodes: string[] = [],
): string[] =>
  _.uniq(
    languageCodes
      .map((languageCode) => normalizeSmartCommsLanguageCode(languageCode))
      .filter(Boolean),
  );

const getSmartCommsContentByLanguage = (
  contentByLanguage: Record<string, any> = {},
  languageCode: string,
): any => {
  const normalizedLanguageCode = normalizeSmartCommsLanguageCode(languageCode);
  const legacyLanguageCode = languageCode.toLowerCase();

  return (
    contentByLanguage[normalizedLanguageCode] ||
    contentByLanguage[languageCode] ||
    contentByLanguage[legacyLanguageCode] ||
    {}
  );
};

type VariantDetails = {
  isControlGroup: boolean;
  id: string;
  isUpdated: boolean;
  isPersonaDataset: boolean;
  hedwigTemplateID?: string;
  isChosen: boolean;
  languages: LanguageData[];
};

type RoundData = {
  id: number;
  variants: VariantDetails[];
};

/**
 * Transforms personal attribute labels in the input box to their corresponding values.
 *
 * @param text - The input text containing attribute labels in curly braces. eg: "{Passenger name}, grab essentials. Shop Now!"
 * @returns The transformed text with labels replaced by their corresponding values. eg: "{{ persona.passenger_name }}, grab essentials. Shop Now!"
 */
export const mapAttributesLabelsToValuesForUCDPayload = (
  text: string,
  wrapInValidValueInMultiBrackets: boolean = true,
): string => {
  if (!text) {
    return '';
  }

  return text.replace(SINGLE_BRACKET_CONTENT_EXTRACTOR, (match, key) => {
    const value = DYNAMIC_ATTRIBUTES_LABEL_TO_VALUES_MAP[key];
    return value || (wrapInValidValueInMultiBrackets ? `{${match}}` : match);
  });
};

export const getPushChannelPayload = ({
  title,
  message,
  metaDataForPushChannel,
}: {
  title: string;
  message: string;
  metaDataForPushChannel?: MetaDataForPushChannel;
}): Record<string, any> => {
  if (!metaDataForPushChannel || _.isEmpty(metaDataForPushChannel)) {
    return { title, message };
  }

  const metaData: Record<string, any> = { imageUrl: metaDataForPushChannel.imageUrl };
  const actionType = metaDataForPushChannel.actionType;

  if ('isActionTypeOrURLUpdated' in metaDataForPushChannel) {
    if (actionType && actionType !== PUSH_CHANNEL_ACTIONS.grabApp) {
      metaData[actionType] = metaDataForPushChannel.actionURL;
    }
  } else {
    // testing preview message
    Object.values(HEDWIG_TEMPLATE_VARIABLES).forEach((variable) => {
      metaData[variable] = metaDataForPushChannel[variable] || '';
    });
  }

  return { title, message, ...metaData };
};

export const getPCIChannelPayload = ({
  title,
  message,
  language,
  metaDataForPCIChannel,
}: {
  title: string;
  message: string;
  language: string;
  metaDataForPCIChannel?: MetaDataForPCIChannel;
}): {
  requestPayload: Record<string, any>;
  isInboxContentUpdated: boolean;
} => {
  if (!metaDataForPCIChannel) {
    return {
      requestPayload: {},
      isInboxContentUpdated: false,
    };
  }
  const initialValues =
    metaDataForPCIChannel[
      SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES
    ];

  // Check whether pci values are updated or not.
  const fields = [
    SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE,
    SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE,
    SMART_CAMPAIGN_FORM_KEYS.PCI_SUBTITLE,
    SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_TEXT,
    SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_LINK,
    SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE_URL,
  ];
  const isInboxContentUpdated =
    initialValues &&
    fields
      .map(
        (field) =>
          (metaDataForPCIChannel[field] &&
            metaDataForPCIChannel[field][language]) !==
          (initialValues[field] && initialValues[field][language]),
      )
      .some((isUpdated) => isUpdated);
  // Since, inbox category value is not inside language object, "isInboxContentUpdated" can't catch any changes in "category"
  const isInboxCategoryUpdated =
    metaDataForPCIChannel[SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY] !==
    (initialValues && initialValues[SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY]);

  const requestPayload = {
    app_name: metaDataForPCIChannel.appName,
    inbox_category: metaDataForPCIChannel.inboxCategory || '',
    inbox_message_content: (metaDataForPCIChannel.inboxMessage && metaDataForPCIChannel.inboxMessage[language]) || '',
    inbox_message_title: (metaDataForPCIChannel.inboxTitle && metaDataForPCIChannel.inboxTitle[language]) || '',
    inbox_title: (metaDataForPCIChannel.inboxTitle && metaDataForPCIChannel.inboxTitle[language]) || '',
    inbox_subtitle: (metaDataForPCIChannel.inboxSubtitle && metaDataForPCIChannel.inboxSubtitle[language]) || '',
    push_message: message,
    push_title: title,
    inbox_button_text: (metaDataForPCIChannel.inboxButtonText && metaDataForPCIChannel.inboxButtonText[language]) || '',
    inbox_button_link: (metaDataForPCIChannel.inboxButtonLink && metaDataForPCIChannel.inboxButtonLink[language]) || '',
    inbox_cover_image: (metaDataForPCIChannel.inboxCoverImageUrl && metaDataForPCIChannel.inboxCoverImageUrl[language]) || '',
  };

  return {
    requestPayload,
    isInboxContentUpdated: isInboxContentUpdated || isInboxCategoryUpdated,
  };
};

// to create 'contents' for 'smartCommsContentMetadata' in request payload.
export const transformContentVariantsInFormForRequestPayload = ({
  channel,
  campaignContent,
  selectedLanguages,
  metaDataForPCIChannel,
  metaDataForPushChannel,
  primaryLanguageContentsInForm,
}: {
  channel: string;
  campaignContent: any;
  selectedLanguages: string[];
  metaDataForPCIChannel?: MetaDataForPCIChannel;
  metaDataForPushChannel?: MetaDataForPushChannel;
  primaryLanguageContentsInForm: PrimaryLanguageContentsInForm;
}): RoundData[] => {
  const normalizedSelectedLanguages =
    normalizeSmartCommsLanguageCodes(selectedLanguages);

  // To get array of languages in a variant.
  const getTitleAndMessageInLanguages = (roundName: string, variantName: string) => {
    let isTitleOrMessageUpdatedForAnyLanguages = false;
    let isPCIFieldsUpdated = false;
    let isPersonaEnabled = false;

    const languages = normalizedSelectedLanguages.map((language) => {
      const roundForCurrentLanguage =
        getSmartCommsContentByLanguage(
          (campaignContent && campaignContent[roundName]) || {},
          language,
        );

      const formVariant = roundForCurrentLanguage[variantName] || {};
      const initialValues =
        formVariant[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES];

      if (!isTitleOrMessageUpdatedForAnyLanguages && initialValues) {
        const isTitleUpdated =
          formVariant[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE] !==
          initialValues[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE];

        const isMessageUpdated =
          formVariant[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE] !==
          initialValues[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE];

        isTitleOrMessageUpdatedForAnyLanguages =
          isTitleUpdated || isMessageUpdated;
      }
      let payload = {};
      const titleAndMessage = {
        title: mapAttributesLabelsToValuesForUCDPayload(
          formVariant[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE],
        ),
        message: mapAttributesLabelsToValuesForUCDPayload(
          formVariant[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE],
        ),
      };

      const isPersonaFound = (
        titleAndMessage.title + titleAndMessage.message
      ).match(DOUBLE_BRACKET_CONTENT_EXTRACTOR);
      if (isPersonaFound) {
        isPersonaEnabled = true;
      }

      switch (channel) {
        case SMART_CAMPAIGN_CHANNELS.push:
          payload = getPushChannelPayload({
            ...titleAndMessage,
            metaDataForPushChannel,
          });
          break;
        case SMART_CAMPAIGN_CHANNELS.pci: {
          const { requestPayload, isInboxContentUpdated } =
            getPCIChannelPayload({
              ...titleAndMessage,
              metaDataForPCIChannel,
              language,
            });
          payload = requestPayload;
          if (isInboxContentUpdated) {
            isPCIFieldsUpdated = isInboxContentUpdated;
          }
          break;
        }
        default:
          break;
      }

      return {
        language: normalizeSmartCommsLanguageCode(language),
        slang: formVariant[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_SLANG],
        payload,
      };
    });

    return {
      languages,
      isPersonaEnabled,
      isTitleOrMessageUpdatedForAnyLanguages:
        isTitleOrMessageUpdatedForAnyLanguages || isPCIFieldsUpdated,
    };
  };

  // To get array of variants in a round.
  const getVariants = (roundName: string): VariantDetails[] => {
    return Object.keys(primaryLanguageContentsInForm.rounds[roundName]).map(
      (variantName) => {
        const variantDetails =
          primaryLanguageContentsInForm.rounds[roundName][variantName];

        const {
          languages,
          isPersonaEnabled,
          isTitleOrMessageUpdatedForAnyLanguages,
        } = getTitleAndMessageInLanguages(roundName, variantName);
        const isActionTypeOrURLUpdated =
          metaDataForPushChannel &&
          metaDataForPushChannel.isActionTypeOrURLUpdated;

        const isPNImageUrlUpdated =
          metaDataForPushChannel && metaDataForPushChannel.isPNImageUrlUpdated;

        const isUpdated =
          variantDetails.isUpdated ||
          (!!variantDetails.hedwigTemplateID &&
            (isTitleOrMessageUpdatedForAnyLanguages ||
              isActionTypeOrURLUpdated ||
              isPNImageUrlUpdated));
        const isControlGroup =
          primaryLanguageContentsInForm.selectedControlGroupVariant ===
          variantName;

        return {
          isControlGroup,
          id: variantDetails.uuid,
          isUpdated: Boolean(isUpdated),
          isPersonaDataset: Boolean(isPersonaEnabled),
          hedwigTemplateID: variantDetails.hedwigTemplateID,
          isChosen: variantDetails.isChosen || isControlGroup,
          languages: languages.filter((languageData) => languageData),
        };
      },
    );
  };

  return Object.keys(primaryLanguageContentsInForm.rounds).map((roundName) => {
    return {
      id: getRoundNumberFromSmartCommsRoundName(roundName),
      variants: getVariants(roundName),
    };
  });
};

// Additional type definitions for Batch 2
type FormInstance = {
  getFieldValue: (fieldName: string) => any;
  getFieldsValue: (fieldNames?: string[]) => Record<string, any>;
  setFieldValue: (fieldName: string | string[], value: any) => void;
  setFieldsValue: (values: Record<string, any>) => void;
  validateFields: (fieldNames?: string[]) => Promise<any>;
  getFieldError: (fieldName: string) => string[];
};

type ContentPortalProduct = {
  key?: string;
  name?: string;
  vertical?: string;
  byLang?: Record<string, {
    name?: string;
    overview?: string;
    reason?: string;
    cta?: string;
  }>;
  whitelist?: {
    cvps?: string[];
  };
};

type CampaignView = {
  campaign?: {
    code?: string;
  };
};

type ProjectView = {
  project?: {
    countryID?: number;
    audience?: string;
    objective?: string;
  };
  projectID?: string;
};

type User = {
  email: string;
};

type Country = {
  countryID: number;
  [key: string]: any;
};

type SmartCampaignPayload = {
  campaign: {
    channelGroup: string;
    budgetSource: string;
    templateType: string;
    smartCommsContentMetadata: {
      contents: any[];
      isGMSEnabled?: boolean;
      keyPointer?: string;
      JTBD?: string;
      callToActions?: string[];
      productOverview?: string;
      reasonToUse?: string;
      tone?: string;
      dynamicAttributes?: string[];
      isSlangEnabled?: boolean;
      isManuallyEnteredContent?: boolean;
      primaryLanguage?: string;
      audienceType?: string;
    };
    smartCommsMetadata: {
      segmentSize?: number;
      xcoEnabled?: boolean;
      [key: string]: any;
    };
    channels?: string[];
    name?: string;
    approver?: string;
    verticals?: string[];
    CVP?: string;
    timezone?: string;
    start?: string;
    startTime?: string;
    end?: string;
    endTime?: string;
    hoursInUTC?: number[];
    controlGroupTime?: string;
    targetSegments?: string[];
    shortName?: string;
    code?: string;
    errorReason?: string;
  };
  status: string;
  user: string;
};

export const isCVPOthersSelected = (vertical: string, cvpValue: string): boolean => {
  return (vertical && !cvpValue) || OTHERS_OF_CVP === cvpValue;
};

export const isContentPortalCVPOthersSelected = (cvpValue?: string): boolean =>
  OTHERS_OF_CVP === cvpValue;

export const isContentPortalProductsEnabled = (): boolean =>
  _.get(window, 'Grab.config.ENABLE_CONTENT_PORTAL_PRODUCTS', false) === true;

const normalizeContentPortalProduct = (product: any): ContentPortalProduct => {
  if (!product) {
    return {};
  }
  if (product.body || product.meta) {
    return productFromApi(product);
  }
  return product;
};

const getContentPortalProductKey = ({
  product,
}: {
  product?: ContentPortalProduct;
}): string => {
  if (!product) {
    return '';
  }

  return product.key || '';
};

export const getSelectedContentPortalProduct = ({
  products,
  selectedValue,
  vertical,
}: {
  products?: any[];
  selectedValue?: string;
  vertical?: string;
  language?: string;
}): ContentPortalProduct | undefined => {
  if (!selectedValue || !products || !products.length) {
    return;
  }

  const normalizedProducts = products.map(normalizeContentPortalProduct);

  return normalizedProducts.find((product) => {
    if (vertical && product.vertical && product.vertical !== vertical) {
      return false;
    }

    return Boolean(product.key && product.key === selectedValue);
  });
};

export const getSlangName = ({
  countryID,
  language,
}: {
  countryID?: number;
  language?: string;
}): string | undefined => {
  if (!countryID || !language) {
    return;
  }

  const slangBasedOnCountry =
    SLANG_DETAILS_BASED_ON_COUNTRY_AND_LANGUAGE[countryID];
  if (!slangBasedOnCountry) {
    return;
  }
  const slangDetails = slangBasedOnCountry[language] || {};
  return slangDetails.name;
};

export const getSmartCampaignPayload = (
  campaignView: CampaignView,
  formInstance: FormInstance,
  projectView: ProjectView,
  user: User,
  countries: Country[],
  selectedLanguages: string[],
  primaryLanguageContentsInForm: PrimaryLanguageContentsInForm,
  contentPortalProducts?: any[],
  shouldUseContentPortalProducts = false,
): SmartCampaignPayload => {
  const normalizedSelectedLanguages =
    normalizeSmartCommsLanguageCodes(selectedLanguages);
  const payload: SmartCampaignPayload = {
    campaign: {
      channelGroup: CREATE_SMART_CAMPAIGN_DEFAULTS.channelGroup,
      budgetSource: CREATE_SMART_CAMPAIGN_DEFAULTS.budgetSource,
      templateType: CREATE_SMART_CAMPAIGN_DEFAULTS.templateType,
      smartCommsContentMetadata: {
        contents: [],
      },
      smartCommsMetadata: {},
    },
    status: SmartCampaignStatus.draft,
    user: user.email,
  };

  const fieldValues: Record<string, any> = {};
  const fieldNames = Object.values(SMART_CAMPAIGN_FORM_KEYS);

  if (formInstance) {
    fieldNames.forEach((fieldName) => {
      fieldValues[fieldName] = formInstance.getFieldValue(fieldName);
    });
  }

  payload.campaign.channels = [fieldValues[SMART_CAMPAIGN_FORM_KEYS.CHANNEL]];
  payload.campaign.name = fieldValues[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME];
  payload.campaign.approver = fieldValues[SMART_CAMPAIGN_FORM_KEYS.APPROVER];

  const vertical = fieldValues[SMART_CAMPAIGN_FORM_KEYS.VERTICAL];
  const cvp = vertical && fieldValues[SMART_CAMPAIGN_FORM_KEYS.CVP];
  const isCVPOthers = shouldUseContentPortalProducts
    ? isContentPortalCVPOthersSelected(cvp)
    : isCVPOthersSelected(vertical, cvp);
  const contentPortalProduct = shouldUseContentPortalProducts
    ? getSelectedContentPortalProduct({
      products: contentPortalProducts,
      selectedValue: cvp,
      vertical,
      language: selectedLanguages[0],
    })
    : undefined;
  const contentPortalProductKey = getContentPortalProductKey({
    product: contentPortalProduct,
  });

  payload.campaign.verticals = vertical ? [vertical] : [];
  let payloadProductOffering = cvp;
  if (isCVPOthers) {
    payloadProductOffering = OTHERS_OF_CVP;
  } else if (shouldUseContentPortalProducts && contentPortalProductKey) {
    payloadProductOffering = contentPortalProductKey;
  }
  payload.campaign.CVP = payloadProductOffering;
  payload.campaign.smartCommsMetadata.isGMSEnabled = false;

  const timezones = getCountryTimezonesFromCountryID(
    (projectView && projectView.project && projectView.project.countryID) || 0,
    countries,
  );

  const startDate = fieldValues[SMART_CAMPAIGN_FORM_KEYS.START_DATE];

  payload.campaign.timezone = timezones && timezones[0].code;
  if (startDate && startDate.isValid()) {
    payload.campaign.start = startDate.format('YYYY-MM-DD');
    const startTime = dayjs(startDate)
      .tz(timezones && timezones[0].tz, true)
      .set('hour', 0)
      .set('minute', 0)
      .set('second', 0)
      .format('YYYY-MM-DDTHH:mm:ssZ');
    payload.campaign.startTime = startTime;
  }

  const endDate = fieldValues[SMART_CAMPAIGN_FORM_KEYS.END_DATE];
  if (endDate && endDate.isValid()) {
    payload.campaign.end = endDate.format('YYYY-MM-DD');

    const endTime = dayjs(endDate)
      .tz(timezones && timezones[0].tz, true)
      .set('hour', 23)
      .set('minute', 59)
      .set('second', 59)
      .format('YYYY-MM-DDTHH:mm:ssZ');
    payload.campaign.endTime = endTime;
  }

  // Convert time window to hours in UTC
  const timeWindow = fieldValues[SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW];
  payload.campaign.hoursInUTC = convertTimeWindowToHoursInUTC(timeWindow, timezones);

  const controlGroupFieldValue =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.CONTROL_GROUP_TIME];
  const controlGroupTime = dayjs(controlGroupFieldValue)
    .tz(timezones && timezones[0].tz, true)
    .set('hour', controlGroupFieldValue.hour())
    .set('minute', controlGroupFieldValue.minute())
    .format('YYYY-MM-DDTHH:mm:ssZ');
  payload.campaign.controlGroupTime = controlGroupTime;

  const segments = fieldValues[SMART_CAMPAIGN_FORM_KEYS.SEGMENTS];
  payload.campaign.targetSegments = segments ? [segments] : [];
  payload.campaign.smartCommsMetadata.segmentSize =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.SEGMENT_SIZE] || undefined;
  payload.campaign.smartCommsMetadata.xcoEnabled = false;
  payload.campaign.smartCommsMetadata[SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN] = fieldValues[SMART_CAMPAIGN_FORM_KEYS.IS_RECURRING_CAMPAIGN];
  payload.campaign.smartCommsMetadata[SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS] = fieldValues[SMART_CAMPAIGN_FORM_KEYS.RECURRENCE_WINDOW_DAYS] || 0;

  const channel = fieldValues[SMART_CAMPAIGN_FORM_KEYS.CHANNEL];
  payload.campaign.channels = channel ? [channel] : [];

  payload.campaign.smartCommsContentMetadata.keyPointer =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.KEY_POINTER];

  payload.campaign.smartCommsContentMetadata.JTBD =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.JTBD];

  // Mystique meta data
  payload.campaign.smartCommsContentMetadata.callToActions =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.CALL_TO_ACTIONS] || [];
  payload.campaign.smartCommsContentMetadata.productOverview =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.PRODUCT_OVERVIEW] || '';
  payload.campaign.smartCommsContentMetadata.reasonToUse =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.REASON_TO_USE] || '';
  payload.campaign.smartCommsContentMetadata.tone =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.TONE] || '';
  payload.campaign.smartCommsContentMetadata.dynamicAttributes =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.DYNAMIC_ATTRIBUTE] || [];
  payload.campaign.smartCommsContentMetadata.audienceType =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.AUDIENCE_TYPE];

  const slangName = getSlangName({
    countryID:
      projectView && projectView.project && projectView.project.countryID,
    language: normalizedSelectedLanguages[0],
  });
  payload.campaign.smartCommsContentMetadata.isSlangEnabled = Boolean(
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.SLANG_TOGGLE] && slangName,
  );

  const smartCommsInitialValues =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES];
  const actionType = fieldValues[SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE];
  const actionURL = fieldValues[SMART_CAMPAIGN_FORM_KEYS.ACTION_URL];
  const pnImageURL = fieldValues[SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL];
  payload.campaign.smartCommsContentMetadata.isManuallyEnteredContent =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.CONTENT_SOURCE] ===
      SMART_CAMPAIGN_CONTENT_SOURCE_MODES.manual || false;

  // Check whether actionType of url is changed.
  const isActionTypeUpdated =
    smartCommsInitialValues &&
    actionType &&
    smartCommsInitialValues[SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE] !==
      actionType;
  const isActionURLUpdate =
    smartCommsInitialValues &&
    actionURL &&
    smartCommsInitialValues[SMART_CAMPAIGN_FORM_KEYS.ACTION_URL] !== actionURL;
  const isActionTypeOrURLUpdated = !!(isActionTypeUpdated || isActionURLUpdate);

  //  Check whether PN image url is updated
  const isPNImageUrlUpdated =
    smartCommsInitialValues &&
    pnImageURL &&
    smartCommsInitialValues[SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL] !==
      pnImageURL;

  const metaDataForPCIChannel: MetaDataForPCIChannel = {
    appName: projectView && projectView.project && projectView.project.audience,
    inboxTitle: fieldValues[SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE] || {},
    inboxMessage: fieldValues[SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE] || {},
    inboxSubtitle: fieldValues[SMART_CAMPAIGN_FORM_KEYS.PCI_SUBTITLE] || {},
    inboxCategory: fieldValues[SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY] || '',
    inboxButtonText:
      fieldValues[SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_TEXT] || {},
    inboxButtonLink:
      fieldValues[SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_LINK] || {},
    inboxCoverImageUrl:
      fieldValues[SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE_URL] || {},
    // Initial values
    [SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES]:
      fieldValues[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES],
  };

  const metaDataForPushChannel: MetaDataForPushChannel = {
    imageUrl: fieldValues[SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL],
    isPNImageUrlUpdated,
    actionType,
    actionURL,
    isActionTypeOrURLUpdated,
  };

  const channelDetails =
    channel &&
    channelOptions.find((channelOption: any) => channelOption.value === channel);
  const campaignContent =
    fieldValues[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT];
  if (campaignContent && Object.keys(campaignContent).length && channel) {
    payload.campaign.smartCommsContentMetadata.contents = [
      {
        rounds: transformContentVariantsInFormForRequestPayload({
          channel,
          campaignContent,
          selectedLanguages: normalizedSelectedLanguages,
          metaDataForPCIChannel,
          metaDataForPushChannel,
          primaryLanguageContentsInForm,
        }),
        channel: channelDetails && channelDetails.hedwigContentType,
      },
    ];
  }

  if (normalizedSelectedLanguages.length) {
    payload.campaign.smartCommsContentMetadata.primaryLanguage =
      normalizedSelectedLanguages[0];
  }

  payload.campaign.shortName = (payload.campaign.name || '').replace(/ /g, '_');
  payload.campaign.code = (campaignView && campaignView.campaign && campaignView.campaign.code) || generatedCampaignCode(
    campaignView,
    payload.campaign.shortName,
    payload.campaign.verticals || [],
    payload.campaign.start,
    projectView,
    countries,
  );

  payload.campaign.errorReason = '';
  return payload;
};

export const generatedCampaignCode = (
  campaignView: CampaignView,
  shortName: string,
  verticals: string[],
  startDate: string,
  projectView: ProjectView,
  countries: Country[],
): string => {
  // campaignView.campaign.code will be undefined in smart campaign duplicate.
  if (campaignView && campaignView.campaign && campaignView.campaign.code) {
    return campaignView.campaign.code;
  }

  const year = startDate ? dayjs(startDate).year() : 0;

  if (!(projectView && projectView.project)) {
    return generateCampaignCode({
      shortName,
      vertical: verticals[0],
      year,
    });
  }

  const countryCode = getCountryCodeFromCountryID(
    projectView.project.countryID || 0,
    countries,
  );
  const audience = projectView.project.audience;
  const objective = projectView.project.objective;

  return generateCampaignCode({
    countryCode,
    year,
    audience,
    objective,
    vertical: verticals[0],
    shortName,
  });
};

// Additional type definitions for Batch 3
type MystiqueMetaData = {
  productOverview: string;
  reasonToUse: string;
  callToAction: string;
};

type MystiquePayload = {
  language: string;
  audience: string;
  product: string;
  marketing: string;
  key_pointers: string;
  reason_to_use: string;
  content_type: string[];
  call_to_action: string;
  num: number;
  tone: string;
  personal_attributes: string[];
  country?: string;
  product_name?: string;
  feedback?: string;
  output?: Record<string, any>;
  ucdPayload?: Record<string, any>;
};

const compactContentGenerationStrings = (values: any[] = []) =>
  values.map((value) => String(value || '').trim()).filter(Boolean);

const splitContentGenerationCallToAction = (value: any) =>
  compactContentGenerationStrings(String(value || '').split(','));

const normalizeUcdContentGenerationProductLanguageCode = (
  languageCode: string = '',
): string => {
  const normalizedLanguageCode = normalizeSmartCommsLanguageCode(languageCode);
  return supportedLanguageCodeByLabel[normalizedLanguageCode] || normalizedLanguageCode;
};

const stringifyPreviousContentGenerationOutput = (output?: Record<string, any>) => {
  if (!output) {
    return '';
  }
  if (typeof output === 'string') {
    return output;
  }
  return JSON.stringify(output);
};

export const buildUcdContentGenerationRequestForMystiqueCall = ({
  baseConfigVersion,
  countryCode,
  endDate,
  mystiquePayload,
  productName,
  productOverride,
  productUUID,
  requestID = `omega-rtc-smart-campaign-content-generation-${uuid4()}`,
  startDate,
  vertical,
}: {
  baseConfigVersion: number;
  countryCode: string;
  endDate?: string;
  mystiquePayload?: MystiquePayload;
  productName?: string;
  productOverride: ProductBodyApi;
  productUUID: string;
  requestID?: string;
  startDate?: string;
  vertical: string;
}): Record<string, any> | undefined => {
  if (!mystiquePayload || baseConfigVersion <= 0 || !countryCode || !productUUID || !vertical || !productOverride) {
    return;
  }

  const callToAction = splitContentGenerationCallToAction(mystiquePayload.call_to_action);
  const productConfigByLang = {
    ...(productOverride.config_by_lang || {}),
  };
  const preferredProductLanguage =
    normalizeUcdContentGenerationProductLanguageCode(mystiquePayload.language) || 'en';
  let productLanguage = preferredProductLanguage;
  if (preferredProductLanguage !== 'en' && !productConfigByLang[preferredProductLanguage]) {
    productLanguage = 'en';
  }
  const currentProductLanguageConfig = productConfigByLang[productLanguage] || {};
  const contentType = (mystiquePayload.content_type && mystiquePayload.content_type[0])
    || UCD_MYSTIQUE_CHANNEL_NAMES.push;
  const channel = contentType === UCD_MYSTIQUE_CHANNEL_NAMES.pci
    ? SMART_CAMPAIGN_CHANNELS.pci
    : SMART_CAMPAIGN_CHANNELS.push;
  const resolvedProductName = productName || mystiquePayload.product_name || productUUID;
  const previousOutput = stringifyPreviousContentGenerationOutput(mystiquePayload.output);
  const input: Record<string, any> = {
    channel,
    country_code: countryCode,
    language: mystiquePayload.language,
    vertical,
    product_offering: resolvedProductName,
    product_uuid: productUUID,
    product_name: resolvedProductName,
    audience: mystiquePayload.audience,
    marketing: mystiquePayload.marketing,
    reason_to_use: mystiquePayload.reason_to_use,
    tone: mystiquePayload.tone,
    content_type: contentType,
    call_to_action: callToAction,
    key_pointers: mystiquePayload.key_pointers || '',
    num: mystiquePayload.num || SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
    personal_attributes: mystiquePayload.personal_attributes || [],
    crm: SMART_CAMPAIGN_MYSTIQUE_CRM,
    l1_persona: SMART_CAMPAIGN_MYSTIQUE_CRM_l1_PERSONA,
    l2_cta: '',
    l2_personas: [],
    use_emoji: true,
    segment_type: '',
    segment_name: '',
    start_date: startDate || '',
    end_date: endDate || '',
  };

  if (previousOutput) {
    input.previous_output = previousOutput;
  }
  if (mystiquePayload.feedback) {
    input.feedback = mystiquePayload.feedback;
  }

  return {
    request_id: requestID,
    base_config_version: baseConfigVersion,
    input,
    test_overrides: {
      product: {
        ...productOverride,
        name: productOverride.name || resolvedProductName,
        vertical: productOverride.vertical || vertical,
        config_by_lang: {
          ...productConfigByLang,
          [productLanguage]: {
            ...currentProductLanguageConfig,
            name: resolvedProductName,
            ci_enabled: currentProductLanguageConfig.ci_enabled || false,
            product_overview: mystiquePayload.product || '',
            reason_to_use: mystiquePayload.reason_to_use || '',
            call_to_action: callToAction,
          },
        },
      },
    },
  };
};

export const getMetaDataForMystiqueCall = ({
  cvp,
  vertical,
  language,
  countryId,
  contentPortalProducts,
  shouldUseContentPortalProducts = false,
}: {
  cvp?: string;
  vertical?: string;
  language?: string;
  countryId?: number;
  contentPortalProducts?: any[];
  shouldUseContentPortalProducts?: boolean;
}): MystiqueMetaData | undefined => {
  if (!vertical) {
    return;
  }
  const normalizedLanguage = normalizeSmartCommsLanguageCode(language);

  const defaultMetaDataForMystiqueCall: MystiqueMetaData = {
    productOverview: PRODUCT_OVERVIEW_MAP[vertical] || '',
    reasonToUse: REASON_TO_USE_MAP[vertical] || '',
    callToAction: CALL_TO_ACTION_MAP[vertical] || '',
  };

  if (shouldUseContentPortalProducts) {
    const selectedProduct = getSelectedContentPortalProduct({
      products: contentPortalProducts,
      selectedValue: cvp,
      vertical,
      language,
    });

    if (!selectedProduct) {
      return defaultMetaDataForMystiqueCall;
    }

    const byLang = selectedProduct.byLang || {};
    const productCopy =
      (language && byLang[language]) ||
      byLang.en ||
      Object.values(byLang).find(Boolean) ||
      {};

    return {
      productOverview: productCopy.overview || '',
      reasonToUse: productCopy.reason || '',
      callToAction: productCopy.cta || '',
    };
  }

  const contentsBasedOnCountryId = mystiqueInputsBasedOnCVP[countryId];
  if (!contentsBasedOnCountryId) {
    return defaultMetaDataForMystiqueCall;
  }

  const contentsBasedOnVerticals = contentsBasedOnCountryId[vertical];
  if (!contentsBasedOnVerticals) {
    return defaultMetaDataForMystiqueCall;
  }

  const contentsBasedOnCVP = contentsBasedOnVerticals[cvp];
  if (!contentsBasedOnCVP) {
    return defaultMetaDataForMystiqueCall;
  }

  const defaultLanguage = 'en';

  const productOverview =
    contentsBasedOnCVP.productOverview[normalizedLanguage] ||
    contentsBasedOnCVP.productOverview[defaultLanguage];
  const reasonToUse =
    contentsBasedOnCVP.reasonToUse[normalizedLanguage] ||
    contentsBasedOnCVP.reasonToUse[defaultLanguage];
  const callToAction =
    contentsBasedOnCVP.callToAction[normalizedLanguage] ||
    contentsBasedOnCVP.callToAction[defaultLanguage];

  return { productOverview, reasonToUse, callToAction };
};

export const generateReqPayloadForMystiqueCall = ({
  lastRoundNumber,
  projectView,
  formInstance,
  language,
  isEditableInputs,
  isSlangEnabled = false,
  slangName = undefined,
  contentPortalProducts,
  shouldUseContentPortalProducts = false,
}: {
  lastRoundNumber?: number;
  projectView?: ProjectView;
  formInstance: FormInstance;
  language: string;
  isEditableInputs?: boolean;
  isSlangEnabled?: boolean;
  slangName?: string;
  contentPortalProducts?: any[];
  shouldUseContentPortalProducts?: boolean;
}): MystiquePayload | undefined => {
  const {
    JTBD,
    channel,
    project,
    vertical,
    feedback,
    keyPointer,
    reasonToUse: reasonToUseUserInput,
    callToAction: callToActionUserInput,
    productOverview: productOverviewUserInput,
    tone,
    dynamicAttribute,
  } = formInstance.getFieldsValue();
  const cvp = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP); // Since we don't have separate cvp selector, form.getFieldsValue() will not return cvp value.

  const isCVPOthers = shouldUseContentPortalProducts
    ? isContentPortalCVPOthersSelected(cvp)
    : isCVPOthersSelected(vertical, cvp);
  if (
    !projectView ||
    !projectView.project ||
    projectView.projectID !== project
  ) {
    return;
  }
  const projectDetails = projectView.project;

  if (!vertical || !channel || !JTBD) {
    return;
  }

  const metaData = getMetaDataForMystiqueCall({
    countryId: projectView.project.countryID,
    vertical,
    cvp,
    language,
    contentPortalProducts,
    shouldUseContentPortalProducts,
  });

  if (!metaData) {
    return;
  }

  const { productOverview, reasonToUse, callToAction } = metaData;

  const marketing = `${JTBD}`;

  // Since channel is a single selector. If tomorrow this changes to a multi-select, this will need to be changed
  const channelDetails = channelOptions.find(
    (option: any) => option.value === channel,
  );

  if (!channelDetails) {
    return;
  }

  const normalizedLanguage = normalizeSmartCommsLanguageCode(language);
  const channelLabel = channelDetails.mystiqueContentType;

  const isUserEditModeActive = isCVPOthers || isEditableInputs;
  const selectedContentPortalProduct = shouldUseContentPortalProducts
    ? getSelectedContentPortalProduct({
      products: contentPortalProducts,
      selectedValue: cvp,
      vertical,
      language,
    })
    : undefined;
  const contentPortalProductName = selectedContentPortalProduct
    ? productDisplayName(selectedContentPortalProduct, language) || ''
    : '';

  const newLineRegex = /\n|\r/g;
  const formattedCallToAction = isUserEditModeActive
    ? (callToActionUserInput && callToActionUserInput.join(',')) || ''
    : (callToAction && callToAction.replace(newLineRegex, ',')) || '';

  // Check if Mystique API v3 is enabled
  const isApiV3Enabled = (window.Grab && window.Grab.config && window.Grab.config.ENABLE_MYSTIQUE_API_V3) || false;

  const payload: MystiquePayload = {
    language:
      isSlangEnabled && slangName
        ? slangName
        : supportedLanguages[normalizedLanguage].label.toLowerCase(),
    audience: projectDetails.audience || '',
    product: isUserEditModeActive ? productOverviewUserInput : productOverview,
    marketing,
    key_pointers: keyPointer,
    reason_to_use: isUserEditModeActive ? reasonToUseUserInput : reasonToUse,
    content_type: [channelLabel],
    call_to_action: formattedCallToAction,
    num: SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
    tone: tone || CREATE_SMART_CAMPAIGN_DEFAULTS.tone,
    personal_attributes: dynamicAttribute || [],
  };

  // Add new fields for API v3 when feature flag is enabled
  if (isApiV3Enabled) {
    // Map country ID to country name
    const countryName = COUNTRY_ID_VS_COUNTRY_NAME[projectDetails.countryID];
    if (countryName) {
      payload.country = countryName;
    }

    // Add Product Name
    const productName = shouldUseContentPortalProducts
      ? contentPortalProductName
      : cvp;
    if (productName) {
      payload.product_name = productName;
    }
  }

  const contentVariantsInForm = formInstance.getFieldValue(
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT,
  );
  // If lastRoundNumber is 0 then, content variants are empty.
  if (!lastRoundNumber || _.isEmpty(contentVariantsInForm)) {
    return {
      ...payload,
      feedback: undefined,
      output: undefined,
    };
  }

  // Transform the content to the format expected by mystique
  const variants = Object.entries(
    contentVariantsInForm[getSmartCommsRoundNameFromNumber(lastRoundNumber)][
      language
    ],
  );

  const filteredVariantsBasedOnSlang = variants.filter((keyAndValue) => {
    const variantObject = keyAndValue[1]; // keyAndValue is array with variantName and its value(object). Value in 0th index will be variantName and 1st index will be a object with title, message etc...
    const slangOfVariant =
      variantObject[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_SLANG];

    if (isSlangEnabled) {
      return slangOfVariant === slangName;
    }

    return slangOfVariant === undefined; // If slangEnabled is false, then, we need pass primary language content, slang in form is only exist if variant is slang based generated content.
  });

  const formattedVariantContentForPayload = filteredVariantsBasedOnSlang.map(
    ([variantName, variantObject]) => {
      const newKey = getVariantNumberFromSmartCommsVariantName(variantName);

      const newValue = {
        Header: mapAttributesLabelsToValuesForUCDPayload(
          variantObject[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE],
        ),
        Message: mapAttributesLabelsToValuesForUCDPayload(
          variantObject[SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE],
        ),
      };

      return [newKey, newValue];
    },
  );

  const output = {
    [channelLabel]: Object.fromEntries(formattedVariantContentForPayload),
  };

  return {
    ...payload,
    feedback,
    output,
  };
};

// Additional type definitions for Batch 4
type TitleAndMessage = {
  title: string;
  message: string;
};

type StatusTrackerCardContent = {
  image: string;
  title: string;
  description: string;
} | null;

type ActionTypeAndURL = {
  actionType: string;
  actionURL: string;
};

/**
 * Transforms personal attribute values in the mystique response or omega(BE) response to their corresponding labels
 *
 * @param value - The input string containing attribute values in curly braces. eg: "{persona.passenger_name}, grab essentials. Shop Now!"
 * @param matchSingleBrackets - Determines whether to match single `{}` or double `{{}}` curly braces.
 * @returns The transformed string with values replaced by their corresponding labels. eg: "{ Passenger name }, grab essentials. Shop Now!"
 */
export const mapAttributesValuesToLabelsInMystiqueResponse = (
  value: string,
  matchSingleBrackets: boolean = true,
): string => {
  if (!value && typeof value !== 'string') {
    return '';
  }

  const regex = matchSingleBrackets
    ? SINGLE_BRACKET_CONTENT_EXTRACTOR
    : DOUBLE_BRACKET_CONTENT_EXTRACTOR;
  return value.replace(regex, (match, key) => {
    const label = DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP[`{{ ${key.trim()} }}`];
    const fallbackValue = !matchSingleBrackets ? `{${key}}` : match; // If matchSingleBrackets false, value of 'match' will be wrapped inside double bracket(eg: {{persona.passenger_age}}) but 'label' should in single bracket according to design.
    return label ? `{${label}}` : fallbackValue;
  });
};

export const getActionTypeAndURLFromMetaDataPayload = (payload: Record<string, any>): ActionTypeAndURL => {
  let actionType: string | undefined;
  let actionURL: string | undefined;
  Object.keys(payload).forEach((key) => {
    if (PUSH_CHANNEL_ACTION_OPTIONS.find((option: any) => option.value === key)) {
      actionType = key;
      actionURL = payload[key];
    }
  });

  return {
    actionType: actionType || PUSH_CHANNEL_ACTIONS.grabApp,
    actionURL: actionURL || '',
  };
};

export const setActionTypeAndActionURL = ({
  formInstance,
  actionType,
  actionURL,
}: {
  formInstance: FormInstance;
  actionType: string;
  actionURL: string;
}): void => {
  // Setting initial values for actionType and actionURL.
  formInstance.setFieldValue(
    [
      SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES,
      SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE,
    ],
    actionType,
  );
  formInstance.setFieldValue(
    [
      SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES,
      SMART_CAMPAIGN_FORM_KEYS.ACTION_URL,
    ],
    actionURL,
  );

  formInstance.setFieldsValue({
    [SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE]: actionType,
    [SMART_CAMPAIGN_FORM_KEYS.ACTION_URL]: actionURL,
  });
};

// return {title: '', message: ''} and also handle metaData on payload based on channel.
export const getTitleMessageAndHandleMetaDataBasedOnChannel = ({
  payload,
  language,
  formInstance,
  channelNameBasedOnHedwig,
}: {
  payload?: Record<string, any>;
  language: string;
  formInstance: FormInstance;
  channelNameBasedOnHedwig: string;
}): TitleAndMessage => {
  const defaultTitleAndMessage: TitleAndMessage = { message: '', title: '' };
  if (!payload) {
    return defaultTitleAndMessage;
  }
  const normalizedLanguage = normalizeSmartCommsLanguageCode(language);

  switch (channelNameBasedOnHedwig) {
    case SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG.push: {
      const actionTypeAndUrl = getActionTypeAndURLFromMetaDataPayload(payload);
      setActionTypeAndActionURL({ formInstance, ...actionTypeAndUrl });

      const values = {
        [SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL]: payload.imageUrl,
      };

      formInstance.setFieldsValue({
        ...values,
        [SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES]: values,
      });

      return {
        title: mapAttributesValuesToLabelsInMystiqueResponse(
          payload.title,
          false,
        ),
        message: mapAttributesValuesToLabelsInMystiqueResponse(
          payload.message,
          false,
        ),
      };
    }
    case SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG.pci: {
      const values = {
        [SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE]: {
          [normalizedLanguage]: payload.inbox_title,
        },
        [SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE]: {
          [normalizedLanguage]: payload.inbox_message_content,
        },
        [SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY]: payload.inbox_category,
        [SMART_CAMPAIGN_FORM_KEYS.PCI_SUBTITLE]: {
          [normalizedLanguage]: payload.inbox_subtitle,
        },
        [SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_TEXT]: {
          [normalizedLanguage]: payload.inbox_button_text,
        },
        [SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_LINK]: {
          [normalizedLanguage]: payload.inbox_button_link,
        },
        [SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE_URL]: {
          [normalizedLanguage]: payload.inbox_cover_image,
        },
      };
      formInstance.setFieldsValue({
        ...values,
        [SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES]: values,
      });
      return {
        message: mapAttributesValuesToLabelsInMystiqueResponse(
          payload.push_message,
          false,
        ),
        title: mapAttributesValuesToLabelsInMystiqueResponse(
          payload.push_title,
          false,
        ),
      };
    }
    default:
      return defaultTitleAndMessage;
  }
};

const CONTENT_PORTAL_CVP_FIELD_LABEL = 'Product Offering/CVP';

export const getContentPortalCVPValidationError = ({
  formInstance,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): string => {
  const vertical = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL);
  const cvp = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP);

  if (!shouldUseContentPortalProducts || !vertical) {
    return '';
  }

  if (!cvp) {
    return `Please select ${CONTENT_PORTAL_CVP_FIELD_LABEL}`;
  }

  if (isContentPortalCVPOthersSelected(cvp)) {
    return '';
  }

  if (hasContentPortalProductsLoadFailed) {
    return `Unable to load ${CONTENT_PORTAL_CVP_FIELD_LABEL} options`;
  }

  if (!hasContentPortalProductsLoaded) {
    return `Please wait for ${CONTENT_PORTAL_CVP_FIELD_LABEL} options to load`;
  }

  const selectedProduct = getSelectedContentPortalProduct({
    products: contentPortalProducts,
    selectedValue: cvp,
    vertical,
  });
  if (selectedProduct) {
    return '';
  }

  return `Please select a valid ${CONTENT_PORTAL_CVP_FIELD_LABEL}`;
};

const shouldValidateContentPortalCVP = ({
  formInstance,
  shouldUseContentPortalProducts = false,
}: {
  formInstance: FormInstance;
  shouldUseContentPortalProducts?: boolean;
}): boolean => {
  return Boolean(
    shouldUseContentPortalProducts &&
      formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL),
  );
};

const isContentPortalCVPValid = ({
  formInstance,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): boolean => {
  return !getContentPortalCVPValidationError({
    formInstance,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    hasContentPortalProductsLoaded,
    hasContentPortalProductsLoadFailed,
  });
};

const getContentPortalCVPFieldNames = ({
  formInstance,
  shouldUseContentPortalProducts = false,
}: {
  formInstance: FormInstance;
  shouldUseContentPortalProducts?: boolean;
}): string[] => {
  if (!shouldValidateContentPortalCVP({ formInstance, shouldUseContentPortalProducts })) {
    return [];
  }

  return [SMART_CAMPAIGN_FORM_KEYS.CVP];
};

const getContentPortalCVPFieldLabelIfInvalid = ({
  formInstance,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): string => {
  if (
    !shouldValidateContentPortalCVP({ formInstance, shouldUseContentPortalProducts }) ||
    isContentPortalCVPValid({
      formInstance,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded,
      hasContentPortalProductsLoadFailed,
    })
  ) {
    return '';
  }

  return CONTENT_PORTAL_CVP_FIELD_LABEL;
};

const getContentPortalCVPFieldStatusIfApplicable = ({
  formInstance,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): FieldStatus[] => {
  if (!shouldValidateContentPortalCVP({ formInstance, shouldUseContentPortalProducts })) {
    return [];
  }

  return [{
    label: CONTENT_PORTAL_CVP_FIELD_LABEL,
    isDone: isContentPortalCVPValid({
      formInstance,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded,
      hasContentPortalProductsLoadFailed,
    }),
  }];
};

const getContentPortalCVPValidationResult = ({
  formInstance,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): { name: string[]; errors: string[] } | undefined => {
  const contentPortalCVPError = getContentPortalCVPValidationError({
    formInstance,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    hasContentPortalProductsLoaded,
    hasContentPortalProductsLoadFailed,
  });

  if (!contentPortalCVPError) {
    return;
  }

  return {
    name: [SMART_CAMPAIGN_FORM_KEYS.CVP],
    errors: [contentPortalCVPError],
  };
};

const getSmartCampaignFieldLabel = (fieldKey: string): string => {
  if (fieldKey === SMART_CAMPAIGN_FORM_KEYS.CVP) {
    return CONTENT_PORTAL_CVP_FIELD_LABEL;
  }

  return SMART_CAMPAIGN_FORM_FIELD_LABELS[fieldKey];
};

export const validateMandatoryFieldsForCreateOrUpdate = async (
  formInstance: FormInstance,
  shouldUseContentPortalProducts = false,
  contentPortalProducts?: any[],
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
): Promise<string[]> => {
  const errors: string[] = [];

  const fieldNames = [
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME,
    SMART_CAMPAIGN_FORM_KEYS.PROJECT,
    SMART_CAMPAIGN_FORM_KEYS.VERTICAL,
    SMART_CAMPAIGN_FORM_KEYS.START_DATE,
    SMART_CAMPAIGN_FORM_KEYS.END_DATE,
    SMART_CAMPAIGN_FORM_KEYS.TONE,
    SMART_CAMPAIGN_FORM_KEYS.AUDIENCE_TYPE,
  ];

  // If time sensitive toggle is on, time window is required
  const isTimeSensitive = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.IS_TIME_SENSITIVE);
  if (isTimeSensitive) {
    fieldNames.push(SMART_CAMPAIGN_FORM_KEYS.TIME_WINDOW);
  }

  try {
    await formInstance.validateFields(fieldNames);
  } catch (error) {
    console.error(error);
  }

  fieldNames.forEach((fieldName) => {
    const fieldError = formInstance.getFieldError(fieldName);

    if (fieldError && fieldError.length) {
      errors.push(fieldError[0]);
    }
  });

  const contentPortalCVPError = getContentPortalCVPValidationError({
    formInstance,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    hasContentPortalProductsLoaded,
    hasContentPortalProductsLoadFailed,
  });
  if (contentPortalCVPError) {
    errors.push(contentPortalCVPError);
  }

  return errors;
};

// Content of status card - Creator View.
export const getCreatorStatusTrackerCardContent = (
  status: string,
  meta: { approverName?: string; startDate?: string }
): StatusTrackerCardContent => {
  switch (status) {
    case SmartCampaignStatus.draft:
      return {
        image: completeSetupIcon,
        title: 'Complete your setup',
        description:
          'Your draft is saved but not sent for approval yet. Continue to complete this campaign creation.',
      };
    case SmartCampaignStatus.pending:
      return {
        image: pendingApprovalIcon,
        title: `Your campaign is pending approval from ${meta.approverName}`,
        description:
          `The approver needs to review this campaign by 23:59 on ${dayjs(meta.startDate).subtract(1, 'day').format('DD MMMM YYYY')}. We will notify you once they approve or reject the campaign. If you need to edit the campaign, please withdraw your approval request.`,
      };
    default:
      return null;
  }
};

const mapUcdContentGenerationPayloadsToMystiqueContent = (
  response: Record<string, any> = {},
) => {
  const payloads = Array.isArray(response.payloads) ? response.payloads : [];

  return payloads.reduce((result, payload) => {
    if (!payload || payload.error) {
      return result;
    }

    const header = payload.title || payload.headline || '';
    const message = payload.body || payload.message || '';
    if (!header || !message) {
      return result;
    }

    const index = Object.keys(result).length + 1;
    return {
      ...result,
      [String(index)]: {
        Header: header,
        Message: message,
      },
    };
  }, {});
};

/**
 *
 * @param mystiqueContents  response of mystique api
 * @param mystiqueChannel channel type
 * @returns 'undefined' if mystique content is not valid,
 *  otherwise, will return array of mystique content:
 *  [{Header: '...', Message: '...'},...]
 */
export const getValidMystiqueContent = ({
  mystiqueContents,
  mystiqueChannel,
  allowPartialContentCount = false,
  numberOfContentsRequested = SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM,
}: {
  mystiqueContents?: Record<string, any>;
  mystiqueChannel?: string;
  allowPartialContentCount?: boolean;
  numberOfContentsRequested?: number;
}): Record<string, any> | undefined => {
  if (_.isEmpty(mystiqueContents) || !mystiqueChannel) {
    return;
  }

  const generatedContents = mystiqueContents && (
    mystiqueContents[mystiqueChannel]
    || (allowPartialContentCount
      ? mapUcdContentGenerationPayloadsToMystiqueContent(mystiqueContents)
      : undefined)
  );
  if (!generatedContents) {
    return;
  }

  let validContentsCount = 0;
  Object.values(generatedContents).forEach((content: any) => {
    if (!content) {
      return;
    }

    if (content.Header && content.Message) {
      validContentsCount += 1;
    }
  });

  if (allowPartialContentCount) {
    return validContentsCount > 0 ? generatedContents : undefined;
  }

  if (validContentsCount !== numberOfContentsRequested) {
    return;
  }

  return generatedContents;
};

// Additional type definitions for Batch 5
type CallToActionOption = {
  label: string;
  value: string;
};

export const getCallToActionList = (callToAction: string): string[] => {
  const newLineRegex = /\n|\r/g;
  return callToAction.split(newLineRegex).filter(Boolean);
};

export const getCallToActionOptionsFromArray = (values: string[]): CallToActionOption[] => {
  const callToActionSet = new Set(values);

  return Array.from(callToActionSet).map((callToAction) => ({
    label: callToAction,
    value: callToAction,
  }));
};

export const isMetadataInputFormFilled = ({
  formInstance,
  isPrimaryInputFormViewActive = true,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  isPrimaryInputFormViewActive?: boolean;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): boolean => {
  const metaValues: string[] = [];
  const channel = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);

  if (
    !isPrimaryInputFormViewActive &&
    channel === SMART_CAMPAIGN_CHANNELS.pci
  ) {
    return isPCIInputFieldsFilled({ formInstance });
  }

  // If channel type is 'push' then, we need to check actionURL too.
  const actionType = formInstance.getFieldValue(
    SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE,
  );

  if (
    channel === SMART_CAMPAIGN_CHANNELS.push &&
    actionType !== PUSH_CHANNEL_ACTIONS.grabApp
  ) {
    metaValues.push(SMART_CAMPAIGN_FORM_KEYS.ACTION_URL);
  }

  const metadataInputFormFields = formInstance.getFieldsValue([
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME,
    SMART_CAMPAIGN_FORM_KEYS.PROJECT,
    SMART_CAMPAIGN_FORM_KEYS.VERTICAL,
    SMART_CAMPAIGN_FORM_KEYS.SEGMENTS,
    SMART_CAMPAIGN_FORM_KEYS.START_DATE,
    SMART_CAMPAIGN_FORM_KEYS.END_DATE,
    SMART_CAMPAIGN_FORM_KEYS.CHANNEL,
    ...getContentPortalCVPFieldNames({
      formInstance,
      shouldUseContentPortalProducts,
    }),
    ...metaValues,
  ]);
  return (
    !_.some(metadataInputFormFields, _.isEmpty) &&
    isContentPortalCVPValid({
      formInstance,
      shouldUseContentPortalProducts,
      contentPortalProducts,
      hasContentPortalProductsLoaded,
      hasContentPortalProductsLoadFailed,
    })
  );
};

export const isPCIInputFieldsFilled = ({
  formInstance,
}: {
  formInstance: FormInstance;
}): boolean => {
  const pciInputFormFields = [
    SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE,
    SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE,
    SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY,
  ];
  const metadataInputFormFields =
    formInstance.getFieldsValue(pciInputFormFields);

  const hasEmptyField = Object.values(metadataInputFormFields).some(
    (value) => !value || _.isEmpty(value) || (typeof value === 'object' && _.some(value, _.isEmpty)),
  );

  return !hasEmptyField;
};

export const getPendingRequiredFields = ({
  formInstance,
  isPrimaryInputFormViewActive = true,
  numberOfSelectedVariants,
  minVariantsRequired,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  isPrimaryInputFormViewActive?: boolean;
  numberOfSelectedVariants: number;
  minVariantsRequired: number;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): string[] => {
  const pendingFields: string[] = [];
  const channel = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);

  if (!isPrimaryInputFormViewActive && channel === SMART_CAMPAIGN_CHANNELS.pci) {
    // Check PCI-specific fields
    const pciInputFormFields = [
      SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE,
      SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE,
      SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY,
    ];
    const pciFieldValues = formInstance.getFieldsValue(pciInputFormFields);

    pciInputFormFields.forEach((fieldKey) => {
      const value = pciFieldValues[fieldKey];
      if (!value || _.isEmpty(value) || (typeof value === 'object' && _.some(value, _.isEmpty))) {
        const label = SMART_CAMPAIGN_FORM_FIELD_LABELS[fieldKey];
        if (label) {
          pendingFields.push(label);
        }
      }
    });

    return pendingFields;
  }

  // Check primary form fields
  const primaryFormFields = [
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME,
    SMART_CAMPAIGN_FORM_KEYS.PROJECT,
    SMART_CAMPAIGN_FORM_KEYS.VERTICAL,
    SMART_CAMPAIGN_FORM_KEYS.SEGMENTS,
    SMART_CAMPAIGN_FORM_KEYS.START_DATE,
    SMART_CAMPAIGN_FORM_KEYS.END_DATE,
    SMART_CAMPAIGN_FORM_KEYS.CHANNEL,
  ];
  primaryFormFields.push(...getContentPortalCVPFieldNames({
    formInstance,
    shouldUseContentPortalProducts,
  }));

  // Add ACTION_URL if push channel and not grabApp action type
  const actionType = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE);
  if (channel === SMART_CAMPAIGN_CHANNELS.push && actionType !== PUSH_CHANNEL_ACTIONS.grabApp) {
    primaryFormFields.push(SMART_CAMPAIGN_FORM_KEYS.ACTION_URL);
  }

  const fieldValues = formInstance.getFieldsValue(primaryFormFields);

  primaryFormFields.forEach((fieldKey) => {
    const value = fieldValues[fieldKey];
    if (_.isEmpty(value)) {
      const label = getSmartCampaignFieldLabel(fieldKey);
      if (label) {
        pendingFields.push(label);
      }
    }
  });
  const contentPortalCVPLabel = getContentPortalCVPFieldLabelIfInvalid({
    formInstance,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    hasContentPortalProductsLoaded,
    hasContentPortalProductsLoadFailed,
  });
  if (contentPortalCVPLabel && !pendingFields.includes(contentPortalCVPLabel)) {
    pendingFields.push(contentPortalCVPLabel);
  }

  // Check variant selection
  if (numberOfSelectedVariants < minVariantsRequired) {
    pendingFields.push(`At least ${minVariantsRequired} variants selected`);
  }

  return pendingFields;
};

export type FieldStatus = {
  label: string;
  isDone: boolean;
};

export const getRequiredFieldsStatus = ({
  formInstance,
  isPrimaryInputFormViewActive = true,
  numberOfSelectedVariants,
  minVariantsRequired,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  formInstance: FormInstance;
  isPrimaryInputFormViewActive?: boolean;
  numberOfSelectedVariants: number;
  minVariantsRequired: number;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): FieldStatus[] => {
  const fieldsStatus: FieldStatus[] = [];
  const channel = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL);

  const isFieldEmpty = (value: unknown): boolean => {
    return !value || _.isEmpty(value) || (typeof value === 'object' && _.some(value, _.isEmpty));
  };

  if (!isPrimaryInputFormViewActive && channel === SMART_CAMPAIGN_CHANNELS.pci) {
    // Check PCI-specific fields
    const pciInputFormFields = [
      SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE,
      SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE,
      SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY,
    ];
    const pciFieldValues = formInstance.getFieldsValue(pciInputFormFields);

    pciInputFormFields.forEach((fieldKey) => {
      const label = SMART_CAMPAIGN_FORM_FIELD_LABELS[fieldKey];
      if (label) {
        const value = pciFieldValues[fieldKey];
        fieldsStatus.push({
          label,
          isDone: !isFieldEmpty(value),
        });
      }
    });

    return fieldsStatus;
  }

  // Check primary form fields (exclude CHANNEL as it's pre-filled)
  const primaryFormFields = [
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME,
    SMART_CAMPAIGN_FORM_KEYS.PROJECT,
    SMART_CAMPAIGN_FORM_KEYS.VERTICAL,
    SMART_CAMPAIGN_FORM_KEYS.SEGMENTS,
    SMART_CAMPAIGN_FORM_KEYS.START_DATE,
    SMART_CAMPAIGN_FORM_KEYS.END_DATE,
  ];
  primaryFormFields.push(...getContentPortalCVPFieldNames({
    formInstance,
    shouldUseContentPortalProducts,
  }));

  // Add ACTION_URL if push channel and not grabApp action type
  const actionType = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.ACTION_TYPE);
  if (channel === SMART_CAMPAIGN_CHANNELS.push && actionType !== PUSH_CHANNEL_ACTIONS.grabApp) {
    primaryFormFields.push(SMART_CAMPAIGN_FORM_KEYS.ACTION_URL);
  }

  const fieldValues = formInstance.getFieldsValue(primaryFormFields);

  primaryFormFields.forEach((fieldKey) => {
    if (fieldKey === SMART_CAMPAIGN_FORM_KEYS.CVP) {
      fieldsStatus.push(...getContentPortalCVPFieldStatusIfApplicable({
        formInstance,
        shouldUseContentPortalProducts,
        contentPortalProducts,
        hasContentPortalProductsLoaded,
        hasContentPortalProductsLoadFailed,
      }));
      return;
    }

    const label = getSmartCampaignFieldLabel(fieldKey);
    if (label) {
      const value = fieldValues[fieldKey];
      fieldsStatus.push({
        label,
        isDone: !_.isEmpty(value),
      });
    }
  });

  // Check variant selection
  fieldsStatus.push({
    label: `At least ${minVariantsRequired} variants selected`,
    isDone: numberOfSelectedVariants >= minVariantsRequired,
  });

  return fieldsStatus;
};

export const insertMystiqueMetaDataToFormItems = ({
  projectView,
  formInstance,
  language,
  setCallToActionOptions,
  contentPortalProducts,
  shouldUseContentPortalProducts = false,
}: {
  projectView?: ProjectView;
  formInstance: FormInstance;
  language: string;
  setCallToActionOptions: (options: CallToActionOption[]) => void;
  contentPortalProducts?: any[];
  shouldUseContentPortalProducts?: boolean;
}): void => {
  const vertical = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.VERTICAL);
  const cvp = formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CVP);
  const isCVPOthers = shouldUseContentPortalProducts
    ? isContentPortalCVPOthersSelected(cvp)
    : isCVPOthersSelected(vertical, cvp);
  const cvpValue = !isCVPOthers ? cvp : '';
  const countryID =
    projectView && projectView.project && projectView.project.countryID;

  const metaData = getMetaDataForMystiqueCall({
    vertical,
    cvp: cvpValue,
    language,
    countryId: countryID,
    contentPortalProducts,
    shouldUseContentPortalProducts,
  });

  if (!metaData) {
    return;
  }

  const newCallToActions = getCallToActionList(metaData.callToAction);
  setCallToActionOptions(getCallToActionOptionsFromArray(newCallToActions));

  formInstance.setFieldValue(
    SMART_CAMPAIGN_FORM_KEYS.CALL_TO_ACTIONS,
    newCallToActions,
  );
  formInstance.setFieldValue(
    SMART_CAMPAIGN_FORM_KEYS.PRODUCT_OVERVIEW,
    metaData.productOverview,
  );
  formInstance.setFieldValue(
    SMART_CAMPAIGN_FORM_KEYS.REASON_TO_USE,
    metaData.reasonToUse,
  );
  formInstance.setFieldValue(
    SMART_CAMPAIGN_FORM_KEYS.TONE,
    CREATE_SMART_CAMPAIGN_DEFAULTS.tone,
  );
};

// Additional type definitions for Batch 6
type CVPOption = {
  label: string;
  key: string;
};

type ScribeVariant = {
  id: string;
  languages: Array<{
    language: string;
    payload: {
      title: string;
      message: string;
    };
  }>;
};

export const getFormattedCVPKey = ({
  vertical,
  cvp,
}: {
  vertical: string;
  cvp: string;
}): string => {
  return `${vertical}-${cvp}`;
};

export const getCVPKeyForOthersBasedOnVertical = (vertical: string): string => {
  return getFormattedCVPKey({ vertical, cvp: OTHERS_OF_CVP });
};

export const getCVPValueFromKey = (cvp: string): string => {
  if (!cvp) {
    return '';
  }

  const parts = cvp.split('-');
  return parts.slice(1).join('-'); // Return everything after the first dash
};

export const getCVPOptionsBasedOnVerticalsAndCountry = ({
  countryID,
  vertical,
}: {
  countryID?: number;
  vertical?: string;
}): CVPOption[] | undefined => {
  if (!countryID || !vertical) {
    return;
  }

  const cvpVerticalMapping = CVP_BASED_ON_VERTICAL_AND_COUNTRY_ID[countryID];
  const cvps = cvpVerticalMapping && cvpVerticalMapping[vertical];

  if (!cvps) {
    return;
  }

  const cvpOptions = cvps.map((cvpValue: string) => ({
    label: _.get(CVP_DISPLAY_NAMES, cvpValue),
    key: getFormattedCVPKey({ vertical, cvp: cvpValue }),
  }));
  const othersInCVP = {
    label: OTHERS_OF_CVP,
    key: getCVPKeyForOthersBasedOnVertical(vertical),
  };

  return [...cvpOptions, othersInCVP];
};

export const getContentPortalCVPOptionsBasedOnVertical = ({
  contentPortalProducts,
  vertical,
  includeOthers = false,
}: {
  contentPortalProducts?: any[];
  vertical?: string;
  includeOthers?: boolean;
}): CVPOption[] | undefined => {
  if (!vertical) {
    return;
  }

  const products = (contentPortalProducts || [])
    .map(normalizeContentPortalProduct)
    .filter((product) => product.vertical === vertical);
  const cvpOptions: CVPOption[] = [];
  const othersInCVP = {
    label: OTHERS_OF_CVP,
    key: getCVPKeyForOthersBasedOnVertical(vertical),
  };

  products.forEach((product) => {
    const productName = productDisplayName(product) || '';
    const productValue = getContentPortalProductKey({ product });

    if (!productValue) {
      return;
    }

    cvpOptions.push({
      label: productName || productValue,
      key: getFormattedCVPKey({
        vertical,
        cvp: productValue,
      }),
    });
  });

  const options = includeOthers ? [...cvpOptions, othersInCVP] : cvpOptions;
  const uniqueOptions = _.uniqBy(options, 'key');

  return uniqueOptions.length ? uniqueOptions : undefined;
};

export const getContentPortalCVPValueAfterProductsLoad = ({
  contentPortalProducts,
  vertical,
  cvp,
}: {
  contentPortalProducts?: any[];
  vertical?: string;
  cvp?: string;
}): string => {
  if (!vertical || !cvp || isContentPortalCVPOthersSelected(cvp)) {
    return cvp || '';
  }

  const selectedProduct = getSelectedContentPortalProduct({
    products: contentPortalProducts,
    selectedValue: cvp,
    vertical,
  });

  return selectedProduct ? cvp : '';
};

export const getContentPortalProductDisplayLabel = ({
  products,
  selectedValue,
  language,
}: {
  products?: any[];
  cvps?: any[];
  selectedValue?: string;
  language?: string;
}): string => {
  const selectedProduct = getSelectedContentPortalProduct({
    products,
    selectedValue,
    language,
  });
  const productName = selectedProduct
    ? productDisplayName(selectedProduct, language) || ''
    : '';

  if (productName) {
    return productName;
  }

  return selectedValue || '';
};

/**
 * Function to send scribe events on mystique content generation( Scribe events to be trigged on response of mystique content generation API response ).
 * @param formInstance
 * @param campaignID   Optional - campaignId may not be available for create smart campaign
 * @param campaignView Optional - CampaignView is state in redux store(data from `useSelector((state) => state.ucd.campaignView)`)
 * @param mystiqueRequestTime  Time of request `useSelector((state) => state.ucd.mystiqueRequestTime)`
 * @param roundNumber Round number of generated contents
 * @param variants Formatted array of variants(contents generated by mystique) eg: [{id: 'uuid',languages: [ { language: language_of_generated_contents, payload: {title: 'sample title', message: 'sample message' } } ] }]
 * @param mystiqueRequestPayload Request payload of mystique content generation API:  useSelector((state) => state.ucd.mystiqueRequestPayload)
 */
export const sendScribeEventForMystiqueContentGeneration = ({
  formInstance,
  campaignID,
  campaignView,
  mystiqueRequestTime,
  roundNumber,
  variants,
  mystiqueRequestPayload,
}: {
  formInstance: FormInstance;
  campaignID?: string;
  campaignView?: CampaignView & { status?: string };
  mystiqueRequestTime?: string;
  roundNumber?: number;
  variants: ScribeVariant[];
  mystiqueRequestPayload?: any;
}): void => {
  scribe.sendEvent(ScribeEventName.GENERATED_CONTENT, {
    campaignID,
    campaignStartDate: formInstance.getFieldValue(
      SMART_CAMPAIGN_FORM_KEYS.START_DATE,
    ),
    campaignEndDate: formInstance.getFieldValue(
      SMART_CAMPAIGN_FORM_KEYS.END_DATE,
    ),
    campaignStatus: campaignView && campaignView.status,
    campaignContent: JSON.stringify({
      campaignID,
      campaignStartDate: formInstance.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.START_DATE,
      ),
      campaignEndDate: formInstance.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.END_DATE,
      ),
      campaignStatus: campaignView && campaignView.status,
      eventTime: mystiqueRequestTime,
      inputData: {
        valueProposition: formInstance.getFieldValue(
          SMART_CAMPAIGN_FORM_KEYS.VALUE_PROPOSITION,
        ),
        topic: formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.TOPIC),
        keyPointer: formInstance.getFieldValue(
          SMART_CAMPAIGN_FORM_KEYS.KEY_POINTER,
        ),
      },
      channelContents: [
        {
          channel: formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.CHANNEL),
          rounds: [
            {
              variants,
              id: roundNumber,
              feedback: formInstance.getFieldValue(
                SMART_CAMPAIGN_FORM_KEYS.FEEDBACK,
              ),
            },
          ],
        },
      ],
      extraData: {
        contentGenService: 'mystique',
        contentGenInput: JSON.stringify(mystiqueRequestPayload),
      },
    }),
  });
};

// Additional type definitions for Batch 7
type ContentData = {
  title: string;
  message: string;
  slang?: string;
};

type ImageData = {
  uid: string;
  url: string;
  name: string;
};


type TransformContentVariantsResponse = {
  isContentEmpty?: boolean;
  callToActionOptions?: CallToActionOption[];
  currentContentSource?: string;
  imageDataBasedOnLanguage?: Record<string, ImageData>;
  lastVariantNumber?: number;
  lastRoundNumber?: number;
  selectedControlGroupVariant?: string;
  rounds?: Record<string, RoundData>;
  primaryLanguage?: string;
  selectedLanguagesInCampaign?: string[];
};

/**
 * Function to fill content values in form.
 * @param  content {title: "", message: ""}.
 * @param  roundName eg: 'round-1'
 * @param  language language-code.
 * @param  variantName  eg: 'variant-1'
 */
export const fillContentContainerFormValues = ({
  content,
  roundName,
  language,
  variantName,
  formInstance,
}: {
  content: ContentData;
  roundName: string;
  language: string;
  variantName: string;
  formInstance: FormInstance;
}): void => {
  const normalizedLanguage = normalizeSmartCommsLanguageCode(language);
  const formItemName = [
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT,
    roundName,
    normalizedLanguage,
    variantName,
  ];
  const formItemInitialValueName = [
    ...formItemName,
    SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_INITIAL_VALUES,
  ];

  formInstance.setFieldValue(
    [
      ...formItemInitialValueName,
      SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE,
    ],
    content.title,
  );
  formInstance.setFieldValue(
    [
      ...formItemInitialValueName,
      SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE,
    ],
    content.message,
  );

  formInstance.setFieldValue(
    [...formItemName, SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE],
    content.title,
  );
  formInstance.setFieldValue(
    [...formItemName, SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE],
    content.message,
  );
  formInstance.setFieldValue(
    [...formItemName, SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_SLANG],
    content.slang,
  );
};

/**
 * Function to used on getCampaign response.
 */
export const transformContentVariantsInCampaignResponse = ({
  campaignView,
  formInstance,
  contentPortalProducts,
  shouldUseContentPortalProducts = false,
}: {
  campaignView?: CampaignView & {
    campaign?: {
      verticals?: string[];
      CVP?: string;
      smartCommsContentMetadata?: {
        JTBD?: string;
        campaignTopic?: string;
        reasonToUse?: string;
        callToActions?: string[];
        productOverview?: string;
        keyPointer?: string;
        isSlangEnabled?: boolean;
        tone?: string;
        isManuallyEnteredContent?: boolean;
        dynamicAttributes?: string[];
        primaryLanguage?: string;
        audienceType?: string;
        contents?: Array<{
          channel: string;
          rounds: Array<{
            id: number;
            variants: Array<{
              id: string;
              isChosen?: boolean;
              isControlGroup?: boolean;
              isUpdated?: boolean;
              hedwigTemplateID?: string;
              languages?: Array<{
                language: string;
                slang?: string;
                payload: Record<string, any>;
              }>;
            }>;
          }>;
        }>;
      };
    };
  };
  formInstance: FormInstance;
  contentPortalProducts?: any[];
  shouldUseContentPortalProducts?: boolean;
}): TransformContentVariantsResponse => {
  const fallBackResponse: TransformContentVariantsResponse = { isContentEmpty: true };

  if (
    !campaignView ||
    !campaignView.campaign ||
    !campaignView.campaign.smartCommsContentMetadata
  ) {
    return fallBackResponse;
  }

  const { campaign } = campaignView;

  // Handling call-to-actions
  const vertical = campaign.verticals ? campaign.verticals[0] : '';
  const cvp = campaign.verticals ? campaign.CVP : '';
  const primaryLanguage = normalizeSmartCommsLanguageCode(
    campaign.smartCommsContentMetadata.primaryLanguage,
  );
  const metaData = getMetaDataForMystiqueCall({
    vertical,
    cvp,
    language: primaryLanguage,
    contentPortalProducts,
    shouldUseContentPortalProducts,
  });

  if (!metaData) {
    return fallBackResponse;
  }

  const callToActionOptions = getCallToActionOptionsFromArray([
    ...getCallToActionList(metaData.callToAction),
    ...(campaign.smartCommsContentMetadata.callToActions || []),
  ]);

  const currentContentSource = campaign.smartCommsContentMetadata
    .isManuallyEnteredContent
    ? SMART_CAMPAIGN_CONTENT_SOURCE_MODES.manual
    : SMART_CAMPAIGN_CONTENT_SOURCE_MODES.automatic;

  formInstance.setFieldsValue({
    JTBD:
      campaign.smartCommsContentMetadata.JTBD ||
      campaign.smartCommsContentMetadata.campaignTopic ||
      '',
    reasonToUse: campaign.smartCommsContentMetadata.reasonToUse || '',
    callToAction: campaign.smartCommsContentMetadata.callToActions || [],
    productOverview: campaign.smartCommsContentMetadata.productOverview || '',
    keyPointer: campaign.smartCommsContentMetadata.keyPointer,
    slangToggle: campaign.smartCommsContentMetadata.isSlangEnabled,
    tone:
      campaign.smartCommsContentMetadata.tone ||
      CREATE_SMART_CAMPAIGN_DEFAULTS.tone,
    contentSource: currentContentSource,
    dynamicAttribute:
      campaign.smartCommsContentMetadata.dynamicAttributes || [],
    audienceType: campaign.smartCommsContentMetadata.audienceType,
  });

  const fallBackResponseWithContentMetaData: TransformContentVariantsResponse = {
    ...fallBackResponse,
    callToActionOptions,
    currentContentSource,
  };
  if (!campaign.smartCommsContentMetadata.contents) {
    return fallBackResponseWithContentMetaData;
  }

  let lastVariantNumberInCampaign = 0;
  let selectedLanguagesInCampaign: string[] = [];
  let variantNameOfControlledGroup: string | undefined;
  const imageDataBasedOnLanguage: Record<string, ImageData> = {};

  const contentsBasedOnChannel = campaign.smartCommsContentMetadata.contents[0]; // Assumed to have only one channel

  if (!contentsBasedOnChannel) {
    return fallBackResponseWithContentMetaData;
  }
  const channelNameBasedOnHedwig = contentsBasedOnChannel.channel;
  const contentVariantsInEachRounds = contentsBasedOnChannel.rounds;
  const formattedRoundDataForPrimaryLanguage: Record<string, RoundData> = {};

  const isPushChannel =
    channelNameBasedOnHedwig === SMART_CAMPAIGN_CHANNELS.push;
  contentVariantsInEachRounds.forEach((roundDetails) => {
    const roundData: RoundData = {};
    const roundName = getSmartCommsRoundNameFromNumber(roundDetails.id); // Need to change here if round id is changed to uuid

    // Loop through variants
    roundDetails.variants.forEach((variantDetails) => {
      lastVariantNumberInCampaign += 1;
      const variantName = getSmartCommsVariantNameFromNumber(
        lastVariantNumberInCampaign,
      );

      if (!variantDetails.languages) {
        return;
      }
      // Looping through languages
      variantDetails.languages.forEach((languageDetails) => {
        const languageCode = normalizeSmartCommsLanguageCode(
          languageDetails.language,
        );
        let slang: string | undefined;

        const titleAndMessage = getTitleMessageAndHandleMetaDataBasedOnChannel({
          formInstance,
          language: languageCode,
          channelNameBasedOnHedwig,
          payload: languageDetails.payload,
        });

        // Handle PCI and PN image
        const imageURL = isPushChannel
          ? formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PN_IMAGE_URL) // image data is appended to form from `getTitleMessageAndHandleMetaDataBasedOnChannel` function.
          : formInstance.getFieldValue([
            SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE_URL,
            languageCode,
          ]);

        if (imageURL) {
          const imageUrlParts = imageURL.split('/').pop();
          const imageName = imageUrlParts ? imageUrlParts.split('?')[0] : '';
          const keyBasedOnChannel = isPushChannel ? PN_IMAGE_KEY : languageCode; // TODO: Need to remove this check, while push channel support multi language image upload
          imageDataBasedOnLanguage[keyBasedOnChannel] = {
            uid: uuid4(),
            url: imageURL,
            name: imageName,
          };
        }

        // handle primary language data.
        if (primaryLanguage === languageCode) {
          slang = languageDetails.slang;
          roundData[variantName] = {
            ...titleAndMessage,
            isChosen: variantDetails.isChosen && !variantDetails.isControlGroup,
            isUpdated: variantDetails.isUpdated || false,
            hedwigTemplateID: variantDetails.hedwigTemplateID,
            uuid: variantDetails.id,
            slang,
          };
        }
        // Finding variant name of controlled group.
        if (primaryLanguage === languageCode && variantDetails.isControlGroup) {
          variantNameOfControlledGroup = variantName;
        }

        const isLanguageExitInSelectedLanguages =
          selectedLanguagesInCampaign.find(
            (language) => language === languageCode,
          );

        if (!isLanguageExitInSelectedLanguages) {
          selectedLanguagesInCampaign = [
            ...selectedLanguagesInCampaign,
            languageCode,
          ];
        }
        fillContentContainerFormValues({
          content: { ...titleAndMessage, slang },
          roundName,
          language: languageCode,
          variantName,
          formInstance,
        });
      });
    });

    formattedRoundDataForPrimaryLanguage[roundName] = roundData;
  });

  return {
    callToActionOptions,
    imageDataBasedOnLanguage,
    lastVariantNumber: lastVariantNumberInCampaign,
    lastRoundNumber: contentVariantsInEachRounds.length,
    selectedControlGroupVariant: variantNameOfControlledGroup,
    rounds: formattedRoundDataForPrimaryLanguage,
    primaryLanguage,
    selectedLanguagesInCampaign,
    currentContentSource,
  };
};

// Additional type definitions for Batch 9
type ReplaceResult = {
  filteredValue: string;
  newCaretPosition: number;
  isTransformed?: boolean;
};

// This function converts text containing personal attributes within curly brackets into HTML.
// skipUnknownAttributeCheck renders labels missing from DYNAMIC_ATTRIBUTES_LABEL_TO_VALUES_MAP as valid
// (used for Audience campaigns, whose persona variables are not in that map).
export const generatePersonalAttributesHTML = (
  inputText: string,
  skipUnknownAttributeCheck: boolean = false,
): string => {
  if (!inputText) {
    return '';
  }

  return inputText.replace(SINGLE_BRACKET_CONTENT_EXTRACTOR, (_, label) => {
    const mappedValue = DYNAMIC_ATTRIBUTES_LABEL_TO_VALUES_MAP[label];
    const value = mappedValue
      || (skipUnknownAttributeCheck ? `{{ ${label.trim()} }}` : undefined);
    const style = value
      ? VALID_PERSONAL_ATTRIBUTE_STYLE
      : INVALID_PERSONAL_ATTRIBUTE_STYLE;

    /**
     * Generate HTML for the text within curly brackets
     * Warning: Be careful while editing html content. Since, a small changes(eg: space, newLine) can introduce bugs in 'SmartCampaignCustomInput' component.
     */
    return `<span class='editable-input-label-container' style="color:${style.color};background-color:${style.backgroundColor};caret-color:${style.color};display:inline-block;"><span class='brackets-in-label' style="color:${style.backgroundColor};" contentEditable="false">{</span>${label}<span class='brackets-in-label' style="color:${style.backgroundColor};" contentEditable="false">}</span><span class='tool-tip-text' contentEditable="false">${value || UNKNOWN_ATTRIBUTE}</span></span>`;
  });
};

/**
 * Function consumed by SmartCampaignCustomInputBox - used to find closest opening curly bracket in a text from given index.
 */
export const findClosestOpeningBracketFromGivenIndex = (
  text: string,
  currentPosition: number,
): number | undefined => {
  if (!text || !currentPosition) {
    return;
  }

  let position: number | undefined;
  for (let i = currentPosition; i >= 0; i -= 1) {
    if (text[i] === '}') {
      break;
    }
    if (text[i] === '{') {
      position = i;
      break;
    }
  }
  return position;
};

/**
 * Function consumed by SmartCampaignCustomInputBox - used to find closest closing curly bracket in a text from given index.
 */
export const findClosestClosingBracketFromGivenIndex = (
  text: string,
  currentPosition: number,
): number | undefined => {
  if (!text || !currentPosition) {
    return;
  }

  let position: number | undefined;
  for (let i = currentPosition; i < text.length; i += 1) {
    if (text[i] === '{') {
      break;
    }
    if (text[i] === '}') {
      position = i;
      break;
    }
  }
  return position;
};

/**
 * Function consumed by SmartCampaignCustomInputBox - used to replace value with label in custom input box.
 * @param text string
 * @param openingBracketIndex  number
 * @param closingBracketIndex number
 * @param isOpeningBracketAdding boolean
 * @returns {filteredValue, newCaretPosition}
 */
export const replaceValueWithLabelInCustomInputBox = ({
  text,
  openingBracketIndex,
  closingBracketIndex,
  keepEmptyStringAfterClosingBracket = false,
}: {
  text: string;
  openingBracketIndex?: number;
  closingBracketIndex?: number;
  keepEmptyStringAfterClosingBracket?: boolean;
}): ReplaceResult => {
  let newCaretPosition = 0;
  if (closingBracketIndex) {
    newCaretPosition = closingBracketIndex + 1;
  } else if (openingBracketIndex) {
    newCaretPosition = openingBracketIndex + 1;
  }

  const defaultReturn: ReplaceResult = {
    filteredValue: text,
    newCaretPosition,
    isTransformed: false,
  };
  if (openingBracketIndex === undefined || closingBracketIndex === undefined) {
    return defaultReturn;
  }

  const textToReplace = text.substring(
    openingBracketIndex + 1,
    closingBracketIndex,
  );

  const mappedReplacement = transformAttributeValueToLabel(textToReplace);

  if (!mappedReplacement) {
    return {
      ...defaultReturn,
      filteredValue: !keepEmptyStringAfterClosingBracket
        ? text
        : `${text.substring(
          0,
          openingBracketIndex,
        )}{${textToReplace}}${keepEmptyStringAfterClosingBracket ? ' ' : ''}${text.substring(closingBracketIndex + 1, text.length)}`,
    };
  }

  const updatedValue = `${text.substring(
    0,
    openingBracketIndex,
  )}{${mappedReplacement}}${keepEmptyStringAfterClosingBracket ? ' ' : ''}${text.substring(closingBracketIndex + 1, text.length)}`;

  return {
    isTransformed: true,
    filteredValue: updatedValue,
    newCaretPosition:
      closingBracketIndex +
      (mappedReplacement.length - textToReplace.length) +
      1,
  };
};

/**
 * Function consumed by SmartCampaignCustomInputBox - used to replace label with value in custom input box.
 * @param  text string
 * @param  openingBracketIndex number
 * @param closingBracketIndex number
 * @param isOpeningBracketRemoving boolean
 * @returns {filteredValue, newCaretPosition}
 */
export const replaceLabelWithValueInCustomInputBox = ({
  text,
  openingBracketIndex,
  closingBracketIndex,
  isOpeningBracketRemoving,
}: {
  text: string;
  openingBracketIndex: number;
  closingBracketIndex: number;
  isOpeningBracketRemoving: boolean;
}): ReplaceResult => {
  const labelText = text.substring(
    isOpeningBracketRemoving ? openingBracketIndex : openingBracketIndex + 1, // If isOpeningBracketRemoving then, already openBracket will be removed, so, we don't need to add a+1 into it to remove '{' from label
    !isOpeningBracketRemoving ? closingBracketIndex : closingBracketIndex - 1, // if isClosingBracketRemoving then, already closingBracket will be removed, so, we don't need to reduce a =1 into it to remove '}' from label
  );

  const valueReplacement = DYNAMIC_ATTRIBUTES_LABEL_TO_VALUES_MAP[labelText];

  if (!valueReplacement) {
    return {
      filteredValue: text,
      newCaretPosition: isOpeningBracketRemoving
        ? openingBracketIndex
        : closingBracketIndex,
    };
  }
  const extractedValue = valueReplacement.replace(
    PERSONAL_ATTRIBUTE_VALUE_MATCHER,
    (match, key) => {
      const valueArray = key.split('.');
      if (valueArray.length !== 2) {
        return match;
      }

      return valueArray[1];
    },
  );

  const updatedValue = `${text.substring(
    0,
    openingBracketIndex,
  )}${!isOpeningBracketRemoving ? '{' : ''}${extractedValue}${isOpeningBracketRemoving ? '}' : ''}${text.substring(closingBracketIndex, text.length)}`;

  return {
    filteredValue: updatedValue,
    newCaretPosition: isOpeningBracketRemoving
      ? openingBracketIndex
      : closingBracketIndex + (extractedValue.length - labelText.length),
  };
};

/**
 * Function to transform personal attribute value to label - used to convert user entered value to label.
 * @param attributeValue value of personal attribute eg: {passenger_name} or {persona.passenger_name} or {Persona.passenger_name} or {Persona.Passenger_Name }
 * @returns label from given value, If label can't extract from given attributeValue then returns undefined
 */
export const transformAttributeValueToLabel = (attributeValue: string): string | undefined => {
  if (!attributeValue) {
    return;
  }

  if (DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP[attributeValue]) {
    return DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP[attributeValue];
  }

  const formattedKey = attributeValue.toLowerCase().trim(); // {{ Persona.Passenger.name}} => {{ persona.passenger.name }}

  if (DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP[formattedKey]) {
    return DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP[formattedKey];
  }

  const keyWithoutDecorators = formattedKey.replace(
    /^\s*\{+\s*persona\.|\s*\{+\s*|\s*\}+\s*|\s*persona\./g,
    '',
  ); // {{ persona.passenger_name }} => passenger_name
  const cleanKey = keyWithoutDecorators.trim() || formattedKey.trim();
  const newValue = `{{ persona.${cleanKey} }}`;

  return DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP[newValue];
};

// Additional type definitions for Batch 10
type CustomInputResult = {
  updatedContent: string;
  startOfCaret: number;
  updateInnerHTML: boolean;
};

// element - HTMLElement
const getSelectionStartAndEnd = (element: HTMLElement): number | undefined => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return;
  }

  const activeRange = selection.getRangeAt(0);
  const startRangeClone = activeRange.cloneRange();
  startRangeClone.selectNodeContents(element);
  startRangeClone.setEnd(activeRange.startContainer, activeRange.startOffset);

  const regExpToRemoveToolTip = new RegExp(
    `\\{\\{.*?\\}\\}|${UNKNOWN_ATTRIBUTE}`,
    'g',
  );

  const start = startRangeClone
    .toString()
    .replace(regExpToRemoveToolTip, '').length;

  return start;
};

const handleBracketAdditionInCustomInput = ({
  isInDeleteMode,
  latestValue,
  currentCaretPosition,
}: {
  isInDeleteMode: boolean;
  latestValue: string;
  currentCaretPosition: number;
}): CustomInputResult | undefined => {
  const isClosingBracketAdding =
    !isInDeleteMode && latestValue[currentCaretPosition - 1] === '}';
  const isOpeningBracketAdding =
    !isInDeleteMode && latestValue[currentCaretPosition - 1] === '{';

  if (!isClosingBracketAdding && !isOpeningBracketAdding) {
    return;
  }

  const openingBracketIndex = isOpeningBracketAdding
    ? currentCaretPosition - 1
    : findClosestOpeningBracketFromGivenIndex(
      latestValue,
      currentCaretPosition - 2, // - 2 is done because we need to give a index before closingBracket
    );
  const closingBracketIndex = isClosingBracketAdding
    ? currentCaretPosition - 1
    : findClosestClosingBracketFromGivenIndex(
      latestValue,
      currentCaretPosition,
    );

  if (openingBracketIndex === undefined || closingBracketIndex === undefined) {
    return;
  }
  const { filteredValue: updatedContent, newCaretPosition } =
    replaceValueWithLabelInCustomInputBox({
      text: latestValue,
      openingBracketIndex,
      closingBracketIndex,
      keepEmptyStringAfterClosingBracket:
        latestValue[currentCaretPosition] === '{',
    });
  return {
    updatedContent,
    updateInnerHTML: true,
    startOfCaret: newCaretPosition,
  };
};

const handleBracketRemovalInCustomInput = ({
  isInDeleteMode,
  currentCaretPosition,
  previousValue,
  latestValue,
}: {
  isInDeleteMode: boolean;
  currentCaretPosition: number;
  previousValue: string;
  latestValue: string;
}): CustomInputResult | undefined => {
  const isClosingBracketRemoving =
    isInDeleteMode && previousValue[currentCaretPosition] === '}';
  const isOpeningBracketRemoving =
    isInDeleteMode && previousValue[currentCaretPosition] === '{';

  // If content is empty inside opening and closing curly bracket.
  const contentIsEmpty =
    isClosingBracketRemoving && previousValue[currentCaretPosition - 1] === '{';

  if (
    (!isClosingBracketRemoving && !isOpeningBracketRemoving) ||
    contentIsEmpty
  ) {
    return;
  }
  const openingBracketIndex = isOpeningBracketRemoving
    ? currentCaretPosition
    : findClosestOpeningBracketFromGivenIndex(
      previousValue,
      currentCaretPosition - 1,
    );
  const closingBracketIndex = isOpeningBracketRemoving
    ? findClosestClosingBracketFromGivenIndex(
      previousValue,
      currentCaretPosition + 1,
    )
    : currentCaretPosition;

  if (openingBracketIndex === undefined || closingBracketIndex === undefined) {
    return;
  }

  const { filteredValue: updatedContent, newCaretPosition } =
    replaceLabelWithValueInCustomInputBox({
      text: latestValue,
      openingBracketIndex,
      closingBracketIndex,
      isOpeningBracketRemoving,
    });

  return {
    updatedContent,
    updateInnerHTML: true,
    startOfCaret: newCaretPosition,
  };
};

const handleTextEditInsideBracketsForCustomInput = ({
  currentCaretPosition,
  latestValue,
}: {
  currentCaretPosition: number;
  latestValue: string;
}): CustomInputResult | undefined => {
  const openingBracketIndex = findClosestOpeningBracketFromGivenIndex(
    latestValue,
    latestValue[currentCaretPosition] === '}'
      ? currentCaretPosition - 1
      : currentCaretPosition,
  );
  const closingBracketIndex = findClosestClosingBracketFromGivenIndex(
    latestValue,
    currentCaretPosition,
  );

  if (openingBracketIndex === undefined || closingBracketIndex === undefined) {
    // !openingBracket || !closingBracket is not done, to handle  openingBracket position with 0
    return;
  }

  const {
    filteredValue: updatedContent,
    newCaretPosition,
    isTransformed,
  } = replaceValueWithLabelInCustomInputBox({
    text: latestValue,
    openingBracketIndex,
    closingBracketIndex,
  });

  if (!isTransformed) {
    return {
      updatedContent: latestValue,
      updateInnerHTML: true,
      startOfCaret: currentCaretPosition,
    };
  }

  return {
    updatedContent,
    updateInnerHTML: true,
    startOfCaret: newCaretPosition,
  };
};

/**
 * Function used by custom input
 * @param { element } HTMLElement
 * @param { isInDeleteMode } boolean
 * @param { previousValue } string
 * @param { latestValue } string
 * @returns { updatedContent, startOfCaret, updateInnerHTML }
 */
export const getCaretPositionAndUpdatedContentForCustomInput = ({
  element,
  isInDeleteMode,
  previousValue,
  latestValue,
}: {
  element: HTMLElement;
  isInDeleteMode: boolean;
  previousValue: string;
  latestValue: string;
}): CustomInputResult => {
  const currentCaretPosition = getSelectionStartAndEnd(element);

  if (currentCaretPosition === undefined) {
    return {
      updatedContent: latestValue,
      startOfCaret: 0,
      updateInnerHTML: false,
    };
  }

  const cursorPositionAndContentOnRemoval = handleBracketRemovalInCustomInput({
    latestValue,
    previousValue,
    isInDeleteMode,
    currentCaretPosition,
  });
  if (cursorPositionAndContentOnRemoval) {
    return cursorPositionAndContentOnRemoval;
  }

  const cursorPositionAndContentOnAdding = handleBracketAdditionInCustomInput({
    latestValue,
    isInDeleteMode,
    currentCaretPosition,
  });
  if (cursorPositionAndContentOnAdding) {
    return cursorPositionAndContentOnAdding;
  }

  const cursorPositionAndContentOnEditInsideBracket =
    handleTextEditInsideBracketsForCustomInput({
      currentCaretPosition,
      latestValue,
    });

  if (cursorPositionAndContentOnEditInsideBracket) {
    return cursorPositionAndContentOnEditInsideBracket;
  }
  return {
    updatedContent: latestValue,
    startOfCaret: currentCaretPosition,
    updateInnerHTML: false,
  };
};

// Additional type definitions for Batch 11
type ValidationField = {
  namePath: string | string[];
  keyName: string;
};


export const getPCIChannelPayloadForHedwigPreview = ({
  formInstance,
  language,
  title,
  message,
}: {
  formInstance: FormInstance;
  language?: string;
  title: string;
  message: string;
}): Record<string, any> => {
  if (!language) {
    return {};
  }

  const metaDataForPCIChannel = {
    inboxTitle:
      formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE) || {},
    inboxMessage:
      formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE) || {},
    inboxCategory:
      formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY) || {},
    inboxButtonText:
      formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_TEXT) ||
      {},
    inboxButtonLink:
      formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_BUTTON_LINK) ||
      {},
    inboxCoverImageUrl:
      formInstance.getFieldValue(
        SMART_CAMPAIGN_FORM_KEYS.PCI_COVER_IMAGE_URL,
      ) || {},
    inboxSubtitle:
      formInstance.getFieldValue(SMART_CAMPAIGN_FORM_KEYS.PCI_SUBTITLE) || {},
  };

  return getPCIChannelPayload({
    title,
    message,
    language,
    metaDataForPCIChannel,
  }).requestPayload;
};

export const validatePCIFieldsForPreviewMessage = ({
  language,
  formInstance,
}: {
  language: string;
  formInstance: FormInstance;
}): string[] => {
  const fields: ValidationField[] = [
    {
      namePath: [SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE, language],
      keyName: 'inbox title',
    },
    {
      namePath: [SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE, language],
      keyName: 'inbox message',
    },
    {
      namePath: SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY,
      keyName: 'inbox category',
    },
  ];

  const errorFields = fields.filter((field) => {
    return !formInstance.getFieldValue(field.namePath);
  });

  return errorFields.map((field) => field.keyName);
};

/**
 *
 * @param primaryLanguageContentsInForm - value of state which we store all rounds and its details.
 * @param formInstance
 * @param language
 * @returns Array of title and messages of selected contents
 */
export const getSelectedVariantsDetails = ({
  language,
  formInstance,
  primaryLanguageContentsInForm,
}: {
  language: string;
  formInstance: FormInstance;
  primaryLanguageContentsInForm: PrimaryLanguageContentsInForm;
}): VariantDetails[] => {
  return Object.keys(primaryLanguageContentsInForm.rounds).flatMap(
    (roundName) => {
      return Object.entries(primaryLanguageContentsInForm.rounds[roundName])
        .map(([variantName, variant]) => {
          if (
            variant.isChosen ||
            primaryLanguageContentsInForm.selectedControlGroupVariant ===
              variantName
          ) {
            const formPath = [
              SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT,
              roundName,
              language,
              variantName,
            ];

            return {
              variantName,
              title: formInstance.getFieldValue([
                ...formPath,
                SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_TITLE,
              ]),
              message: formInstance.getFieldValue([
                ...formPath,
                SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_CONTENT_MESSAGE,
              ]),
            };
          }

          return undefined;
        })
        .filter((value) => Boolean(value));
    },
  );
};

/* Function used to validate the fields for content generation
 * @param form form instance to validate
 * @param isMystiqueContentEmpty Flag indicating if Mystique content is empty
 * @param isCVPOthers  Flag indicating if CVP is set to "Others"
 * @param isEditableInputs  Flag indicating if inputs are in editable mode
 * @returns {Promise<Object>} Validation result object
 */
export const validateSmartCampaignFields = async ({
  form,
  isMystiqueContentEmpty,
  isCVPOthers,
  isEditableInputs,
  shouldUseContentPortalProducts = false,
  contentPortalProducts,
  hasContentPortalProductsLoaded = false,
  hasContentPortalProductsLoadFailed = false,
}: {
  form: FormInstance;
  isMystiqueContentEmpty: boolean;
  isCVPOthers: boolean;
  isEditableInputs: boolean;
  shouldUseContentPortalProducts?: boolean;
  contentPortalProducts?: any[];
  hasContentPortalProductsLoaded?: boolean;
  hasContentPortalProductsLoadFailed?: boolean;
}): Promise<any[]> => {
  let errorFields: any[] = [];
  try {
    const fieldsToValidate = [
      ...FIELDS_TO_VALIDATE_FOR_GENERATE_CONTENTS,
      ...(!isMystiqueContentEmpty ? [SMART_CAMPAIGN_FORM_KEYS.FEEDBACK] : []),
      ...(isCVPOthers || isEditableInputs
        ? MYSTIQUE_META_DATA_FIELD_NAMES
        : []),
    ];
    await form.validateFields(fieldsToValidate);
  } catch (error) {
    errorFields = (error || {}).errorFields || [];
  }

  const contentPortalCVPError = getContentPortalCVPValidationResult({
    formInstance: form,
    shouldUseContentPortalProducts,
    contentPortalProducts,
    hasContentPortalProductsLoaded,
    hasContentPortalProductsLoadFailed,
  });
  if (contentPortalCVPError) {
    errorFields.push(contentPortalCVPError);
  }

  return errorFields;
};

export const getLanguageNameFromCode = ({
  languageCode,
}: {
  languageCode: string;
}): string | undefined => {
  const languageDetails =
    supportedLanguages[normalizeSmartCommsLanguageCode(languageCode)];

  if (!languageDetails || !languageDetails.label) {
    return;
  }
  return (languageDetails.label || '').toLowerCase();
};

export const getPushChannelActionLabelBasedOnValue = (value: string): string => {
  if (!value) {
    return '';
  }
  const action = PUSH_CHANNEL_ACTION_OPTIONS.find(
    (option: any) => option.value === value,
  );
  return (action && action.label) || '';
};

export const getTheNumberOfPermutations = ({
  campaignDurationDays,
  numberOfContentVariants,
  segmentSize,
}: {
  campaignDurationDays: number;
  numberOfContentVariants: number;
  segmentSize: number;
}): number => {
  const numberOfPermutations =
    campaignDurationDays * 14 * numberOfContentVariants * segmentSize;

  return numberOfPermutations;
};

export const getCombinedSegmentValue = (segment: {
  segmentName?: string;
  namespace?: string;
}): string => {
  if (!segment || !segment.segmentName) return '';

  return segment.namespace
    ? `${segment.namespace}.${segment.segmentName}`
    : segment.segmentName;
};

export const copyTextToClipBoard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    console.error(err);
  }
};

export const getAdditionalInfoOfDynamicAttributes = (attribute: string): {
  content?: string;
  lastUpdatedTime?: string;
} => {
  const additionalDetailsOfAttributes: {
    content?: string;
    lastUpdatedTime?: string;
  } = {};
  const attributesContent = dynamicAttributesDataSet[attribute];

  if (attributesContent) {
    additionalDetailsOfAttributes.content =
      attributesContent.meta &&
      attributesContent.meta.map((item: any) => item.content).join(' • ');
    additionalDetailsOfAttributes.lastUpdatedTime =
      attributesContent.lastUpdated;
  }

  return additionalDetailsOfAttributes;
};
